<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte'
  import { get } from 'svelte/store'

  // UI Components
  import EditorContainer from './EditorContainer.svelte'
  import EditorHeader from './EditorHeader.svelte'
  import EditorFooter from './EditorFooter.svelte'
  import Toolbar from './editor/Toolbar.svelte'
  import TextManager from './TextManager.svelte'
  import TextDecorationEditor from './editors/TextDecorationEditor.svelte'
  import TextAreaEditor from './editors/TextAreaEditor.svelte'
  import TipTapMarkdownEditor from './editors/TipTapMarkdownEditor.svelte'
  import ReadingModeToolbar from './editors/ReadingModeToolbar.svelte'
  import ImageViewer from './ImageViewer.svelte'
  import ViewModeToggle from './ViewModeToggle.svelte'
  import ErrorDisplay from './ErrorDisplay.svelte'
  import StatusMessage from './StatusMessage.svelte'
  import SplitEditor from './editor/SplitEditor.svelte'
  import GenerationBar from './editor/GenerationBar.svelte'
  import UnifiedPromptPanel from './editor/UnifiedPromptPanel.svelte'
  import SearchBar from './SearchBar.svelte'
  import { getCaretCoordinates } from '$lib/utils/getCaretCoordinates'
  
  // File System Components
  import FileExplorer from './FileExplorer.svelte'
  import FileSearchView from './FileSearchView.svelte'
  import TabBar from './TabBar.svelte'
  import FileChangeModal from './FileChangeModal.svelte'
  import PDFExportDialog from './PDFExportDialog.svelte'
  import { fileStore, activeTab, hasUnsavedChanges } from '$lib/stores/fileStore'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { fileSearchStore } from '$lib/stores/fileSearchStoreOptimized'
  import { explorerSettingsStore, updateExplorerSettings, createSvelteStore } from '$lib/stores/uiSettingsStore'
  import { useDrag } from '$lib/hooks/useDrag.svelte'
  import { getFileSystemWatcher } from '$lib/services/FileSystemWatcher'
  
  // Icons for file explorer controls
  import { PanelLeft, Folder, Search } from 'lucide-svelte'
  
  // Initialize on mount
  onMount(async () => {
    // Check if we're in demo mode
    const urlParams = new URLSearchParams(window.location.search)
    isDemoMode = urlParams.get('demo') === 'true'

    // ContentIndex is now initialized automatically by vaultStore
    // No need to manually build index
    
    // Expose refresh function to window for debugging
    if (typeof window !== 'undefined') {
      (window as any).refreshFileCache = () => fileStore.refreshCache()
    }
    
    // Direct global keyboard shortcut handler for Cmd+Shift+F
    const handleGlobalShortcut = (e: KeyboardEvent) => {
      // Check for Cmd+Shift+F (Mac) or Ctrl+Shift+F (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault()
        e.stopPropagation()
        
        // Show explorer if hidden
        if (!showFileExplorer) {
          showFileExplorer = true
        }
        
        // Switch to search mode
        explorerMode = 'search'
        
        // Dispatch event to focus search input
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('focus-file-search'))
        }, 100)
        
        return false
      }
    }
    
    // Add to window at capture phase with highest priority
    window.addEventListener('keydown', handleGlobalShortcut, { capture: true, passive: false })
    
    // Listen for scroll to search match events from FileSearchView
    const handleScrollToMatch = (event: CustomEvent) => {
      if (event.detail && event.detail.position != null) {
        scrollToMatch(event.detail.position)
      }
    }
    
    // Listen for performSearch events from FileSearchView
    const handlePerformSearch = (event: CustomEvent) => {
      if (event.detail && event.detail.searchTerm) {
        const { searchTerm: term, matchInfo } = event.detail
        
        // Show search bar and set the search term
        searchStore.show()
        searchTerm = term
        
        // Get the appropriate text based on view mode
        let searchText = editorText
        let yamlOffset = 0
        
        // If in wysiwyg mode, we need to account for YAML removal
        if (viewMode === 'wysiwyg' && editorText.includes('---\n')) {
          // Find YAML frontmatter end
          const yamlMatch = editorText.match(/^---\n[\s\S]*?\n---\n/)
          if (yamlMatch) {
            yamlOffset = yamlMatch[0].length
            // For wysiwyg mode, search in text without YAML
            searchText = editorText.substring(yamlOffset)
          }
        }
        
        // Search in the appropriate text
        const matches = searchStore.search(searchText, term)
        
        // If we have match info and matches were found, navigate to the specific match
        if (matchInfo && matches.length > 0) {
          // Small delay to ensure UI is updated
          setTimeout(() => {
            let targetMatchIndex = 0
            
            if (currentTab) {
              // Calculate position, accounting for YAML offset if in wysiwyg mode
              const lines = editorText.split('\n')
              let currentPos = 0
              
              // Adjust line number (FileSearchView might count title as line 0)
              const adjustedLineNumber = matchInfo.lineNumber > 0 ? matchInfo.lineNumber - 1 : 0
              
              for (let lineIdx = 0; lineIdx < lines.length && lineIdx < adjustedLineNumber; lineIdx++) {
                currentPos += lines[lineIdx].length + 1 // +1 for newline
              }
              
              // Now currentPos is at the start of our target line in original text
              let expectedPos = currentPos + matchInfo.startIndex
              
              // If in wysiwyg mode, subtract YAML offset
              if (viewMode === 'wysiwyg' && yamlOffset > 0) {
                expectedPos = Math.max(0, expectedPos - yamlOffset)
              }
              
              // Find which match is closest to our expected position
              let closestDistance = Infinity
              for (let i = 0; i < matches.length; i++) {
                const distance = Math.abs(matches[i].start - expectedPos)
                if (distance < closestDistance) {
                  closestDistance = distance
                  targetMatchIndex = i
                }
              }
            }
            
            // Navigate to the specific match
            searchStore.goToMatch(targetMatchIndex)
            
            // Scroll to the match - works for both textarea and TipTap
            scrollToMatch(0) // Position parameter not used anymore
          }, 150)
        } else if (matches.length > 0) {
          // No specific match info, just scroll to first match
          setTimeout(() => {
            searchStore.goToMatch(0)
            scrollToMatch(0)
          }, 150)
        }
      }
    }
    
    // Listen for clearSearchTerm events from FileSearchView
    const handleClearSearchTerm = () => {
      searchTerm = ''
      lastSearchedText = ''
      lastSearchTerm = ''
      searchStore.clear()
      searchStore.hide()
    }
    
    window.addEventListener('scrollToSearchMatch', handleScrollToMatch as EventListener)
    window.addEventListener('performSearch', handlePerformSearch as EventListener)
    window.addEventListener('clearSearchTerm', handleClearSearchTerm)
    
    // Setup file system change detection
    let fsWatcherCleanup: (() => void) | null = null
    const isElectron = typeof window !== 'undefined' && (window as any).api
    
    // Initialize FileSystemWatcher for both web and Electron
    const fsWatcher = getFileSystemWatcher()
    
    // Start the file watcher when vault is initialized
    const vaultState = get(vaultStore)
    if (vaultState.isInitialized) {
      await fsWatcher.start()
    }
    
    // Only web needs the modal for file changes
    if (!isElectron) {
      // Listen for file system changes (for modal)
      const handleFileSystemChange = () => {
        const changedCount = fsWatcher.getChangedFilesCount()

        // Always refresh file explorer when file system changes
        window.dispatchEvent(new CustomEvent('file-explorer-refresh', {
          detail: {
            force: true,
            forceFullScan: true
          }
        }))
        
        if (changedCount > 0) {
          fileChangeCount = changedCount
          needsPermission = fsWatcher.needsPermission()
          showFileChangeModal = true
        }
      }
      
      window.addEventListener('file-system-changed', handleFileSystemChange)
      
      // Also listen for vault changes to start watcher
      const unsubscribeVault = vaultStore.subscribe(async (state) => {
        if (state.isInitialized && !fsWatcher.isRunning()) {
          await fsWatcher.start()
        }
      })
      
      fsWatcherCleanup = () => {
        window.removeEventListener('file-system-changed', handleFileSystemChange)
        unsubscribeVault()
        fsWatcher.stop()
      }
    } else {
      // Electron also needs to listen for file system changes
      const handleFileSystemChange = () => {
        // Always refresh file explorer when file system changes
        window.dispatchEvent(new CustomEvent('file-explorer-refresh', {
          detail: {
            force: true,
            forceFullScan: true
          }
        }))
      }
      
      window.addEventListener('file-system-changed', handleFileSystemChange)
      
      // Electron also needs vault subscription to start watcher
      const unsubscribeVault = vaultStore.subscribe(async (state) => {
        if (state.isInitialized && !fsWatcher.isRunning()) {
          await fsWatcher.start()
        }
      })
      
      fsWatcherCleanup = () => {
        window.removeEventListener('file-system-changed', handleFileSystemChange)
        unsubscribeVault()
        fsWatcher.stop()
      }
    }
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleGlobalShortcut, { capture: true } as any)
      window.removeEventListener('scrollToSearchMatch', handleScrollToMatch as EventListener)
      window.removeEventListener('performSearch', handlePerformSearch as EventListener)
      window.removeEventListener('clearSearchTerm', handleClearSearchTerm)
      if (fsWatcherCleanup) {
        fsWatcherCleanup()
      }
    }
  })
  
  // Controllers - modular components handling specific responsibilities
  import AIFeatureController from './controllers/AIFeatureController.svelte'
  import ClipboardController from './controllers/ClipboardController.svelte'
  import CorrectionController from './controllers/CorrectionController.svelte'
  import GrammarCheckController from './controllers/GrammarCheckController.svelte'
  import TextEditingController from './controllers/TextEditingController.svelte'
  import InlineViewController from './controllers/InlineViewController.svelte'
  import HighlightOverlay from './HighlightOverlay.svelte'
  import SelectionOverlay from './SelectionOverlay.svelte'
  
  // Stores and Services
  import { textStore, errorStore, statsStore, grammarActions } from '$lib/stores/grammar'
  import { settingsStore } from '$lib/stores/settings'
  import { subscriptionStore } from '$lib/stores/subscription'
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import { inlineViewStore, type InlineView } from '$lib/stores/inlineView'
  import { postingPromptStore } from '$lib/stores/postingPrompt'
  import { unifiedPromptStore } from '$lib/stores/unifiedPrompt'
  import { sidebarStore } from '$lib/stores/sidebarStore'
  import { searchStore } from '$lib/stores/searchStore'
  import { editorViewStore, currentViewMode, type EditorViewMode } from '$lib/stores/editorViewStore'
  import { EditorStateService } from '$lib/services/EditorStateService'
  import { errorService } from '$lib/services/ErrorService'
  import { useAutoHide } from '$lib/hooks/useTimer'
  import { useStorageListener, useCustomEvent, useKeyboard } from '$lib/hooks/useEventListener'
  import type { BatchProgress } from '../services/batchCheckService'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  import DiffMatchPatch from 'diff-match-patch'
  
  /**
   * MainEditorRefactored: Orchestrates child components and controllers
   * Single Responsibility: Component composition and coordination only
   * All business logic is delegated to specialized controllers
   */
  
  // State from stores
  let textState = $state(get(textStore))
  let errorState = $state(get(errorStore))
  let stats = $state(get(statsStore))
  let settings = $state(get(settingsStore))
  let subscription = $state(get(subscriptionStore))
  
  // Local UI state
  let editorText = $state('')
  let viewMode = $state(get(currentViewMode))
  let isTitleFocused = $state(false)
  let hasChecked = $state(false)
  let textManager = $state<TextManager | null>(null)

  // Detect if we're in an iframe
  const isInIframe = typeof window !== 'undefined' && window !== window.parent

  // Check if we're in demo mode
  let isDemoMode = $state(false)

  let editorHeader: EditorHeader | null = null
  let statusMessage = $state<string | null>(null)
  let batchProgress = $state<BatchProgress | null>(null)
  
  // Track original view mode when grammar check starts
  let originalViewModeForGrammarCheck: EditorViewMode | null = null
  let showSplitEditor = $state(false)
  let splitEditorMode = $state<'paraphrase' | 'expand' | 'shrink' | 'continue' | null>(null)
  let aiSegments = $state<any[]>([])
  let hoveredSegmentId = $state<string | null>(null)
  let hoveredErrorId = $state<string | null>(null)
  let hasTextSelection = $state(false)
  let correctionStates = $state<Map<string, AppliedCorrectionInfo>>(new Map())
  let lastHistoryText = $state<string | null>(null)
  let hasInitialAIState = $state(false)
  let isAIProcessing = false
  let allCorrectionsApplied = $state(false)
  let processedErrorCount = $state(0)
  let initialErrorCount = $state(0)
  let inlineViews = $state<InlineView[]>([])
  let textareaElement = $state<HTMLTextAreaElement | null>(null)
  let titleTextareaElement = $state<HTMLTextAreaElement | null>(null)
  let titleDuplicateWarning = $state('')
  let duplicateWarningTimestamp = $state<number | null>(null) // When warning was first shown
  let duplicateWarningTimeout = $state<ReturnType<typeof setTimeout> | null>(null) // Timeout for clearing warning
  const DUPLICATE_WARNING_MIN_DURATION = 2500 // Minimum duration to show warning (ms)
  let originalTitle = $state('') // Store original title when focus
  let hasDuplicateTitle = $state(false) // Track if current title is duplicate
  let tipTapEditor = $state<TipTapMarkdownEditor | null>(null)
  let showGenerationPrompt = $state(false)
  let selectedTextForGeneration = $state<string>('')
  let showSelectionOverlay = $state(false)
  let generationBarHeight = $state(0)
  let unifiedPromptHeight = $state(0)
  
  // Store last selection for AI features (before shortcuts clear selection)
  let lastSelection: { start: number, end: number, text: string } | null = null
  let lastSelectionTimer: ReturnType<typeof setTimeout> | null = null
  let generationSelectionRange = $state<{ start: number; end: number } | null>(null)
  let isIndividualSegmentUpdate = $state(false)
  let isBulkProcessing = $state(false)  // Flag to prevent SegmentProcessorController interference during bulk actions
  let lastCursorPosition = $state<{ start: number; end: number }>({ start: 0, end: 0 })
  let unifiedPromptState = $state(get(unifiedPromptStore))
  
  // Search state
  let searchState = $state(get(searchStore))
  let searchTerm = $state('')
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let lastSearchedText = $state('') // Track last searched text for memoization
  
  // Fullscreen state
  let isFullscreen = $state(false)
  let lastSearchTerm = $state('') // Track last search term
  
  // File change modal state
  let showFileChangeModal = $state(false)
  let fileChangeCount = $state(0)
  let needsPermission = $state(false)
  
  // PDF export state
  let showPDFExport = $state(false)
  
  // File operations
  async function handleReloadFiles() {
    showFileChangeModal = false // Close modal immediately
    
    // Request permission first if we need it
    if (needsPermission) {
      const granted = await vaultStore.requestPermission()
      if (!granted) {
        return
      }
    }
    
    // Force reload all files (bypass cache)
    await fileStore.reloadAllFiles(true) // Pass true to force bypass cache
    
    // Refresh file explorer
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
    }
    
    // Clear the file watcher's changed files count
    const fsWatcher = getFileSystemWatcher()
    fsWatcher.clearChangedCount()
  }
  
  async function handleRequestPermission() {
    const fsWatcher = getFileSystemWatcher()
    const granted = await fsWatcher.requestPermissionRefresh()
    if (granted) {
      // Permission granted, files will be automatically reloaded
    }
  }
  
  // Derive search matches reactively for better performance
  let searchMatches = $derived.by(() => {
    // Return cached matches if text and term haven't changed
    if (editorText === lastSearchedText && searchTerm === lastSearchTerm) {
      return searchState.matches.map(m => ({
        start: m.start,
        end: m.end
      }))
    }
    
    // Return empty if no search term
    if (!searchTerm || !searchState.isVisible) {
      return []
    }
    
    // Otherwise return current matches
    return searchState.matches.map(m => ({
      start: m.start,
      end: m.end
    }))
  })
  
  // File System State - use $state with subscriptions for better reactivity
  let currentTab = $state(get(activeTab))
  let hasUnsaved = $state(get(hasUnsavedChanges))
  
  // Track tab switching for state management
  let previousTabId = $state<string | null>(null)
  let previousEditorTabId = $state<string | null>(null) // Separate tracker for $effect
  let isRestoringState = false
  
  // Subscribe to activeTab changes and manage check states
  $effect(() => {
    const unsubscribe = activeTab.subscribe(tab => {
      // Skip if we're in the middle of restoring state
      if (isRestoringState) {
        currentTab = tab
        return
      }

      // Check if this is an actual tab switch (compare with previousTabId BEFORE updating it)
      const isTabSwitch = previousTabId !== tab?.id

      // Save previous tab's state before switching
      if (previousTabId && previousTabId !== tab?.id && !isRestoringState) {
        fileStore.saveTabCheckState(previousTabId, errorState, inlineViews)
        // Clear search completely when switching tabs
        searchStore.hide()
        searchStore.clear()
        searchTerm = ''  // Clear the search term bound to SearchBar
        lastSearchedText = ''  // Reset search tracking
        lastSearchTerm = ''
      }

      currentTab = tab

      // Clear title duplicate warning and original title when switching tabs
      if (isTabSwitch) {
        titleDuplicateWarning = ''
        originalTitle = ''
      }

      // Load new tab's state
      if (tab?.id && tab.id !== previousTabId) {
        isRestoringState = true
        // CRITICAL: Also set isSyncingTabContent to prevent textStore from updating tab
        isSyncingTabContent = true
        // CRITICAL: Clear isCreatingNewDocument when switching tabs
        // This prevents the flag from blocking updates when user quickly switches after creating
        isCreatingNewDocument = false

        // Load posting prompt for the new tab
        postingPromptStore.loadForTab(tab.id)

        // Force update the text content first
        if (tab.content !== undefined) {
          editorText = tab.content
          textStore.set({
            original: tab.content,
            current: tab.content,
            corrected: '',
            preview: ''
          })
        }

        // CRITICAL: Update previousTextUpdateTabId immediately to prevent old content from being written back
        // This must happen BEFORE isSyncingTabContent is cleared
        previousTextUpdateTabId = tab.id
        
        const savedState = fileStore.getTabCheckState(tab.id)
        if (savedState?.errorState || savedState?.inlineViews) {
          // Restore saved state
          if (savedState.errorState) {
            // Validate that errors match the current text content
            const validatedErrors = savedState.errorState.errors.filter(error => {
              // Check if error position is within text bounds
              const textLength = tab.content?.length || 0
              return error.start >= 0 && error.end <= textLength
            })
            
            errorState = {
              ...savedState.errorState,
              errors: validatedErrors
            }
            
            // Update store after a delay to ensure text is loaded
            setTimeout(() => {
              if (!isRestoringState) return
              errorStore.set(errorState)
            }, 50)
          }
          
          if (savedState.inlineViews) {
            // Validate that inline views match the current text content
            const textLength = tab.content?.length || 0
            const validatedViews = savedState.inlineViews.filter(view => {
              // Check position bounds
              if (view.position.start < 0 || view.position.end > textLength) {
                return false
              }
              
              // Check if the text at the saved position matches the original text
              const currentTextAtPosition = (tab.content || '').substring(
                view.position.start,
                view.position.end
              )
              
              // Keep the view if text matches or if it's already applied
              return currentTextAtPosition === view.originalText || view.status === 'applied'
            })
            
            inlineViews = validatedViews
            
            // Update store after a delay to ensure text is loaded
            setTimeout(() => {
              if (!isRestoringState) return
              // Restore the views with their original state intact
              inlineViewStore.restoreViews(validatedViews)
              
              // Reset restoration flag after restoration is complete
              setTimeout(() => {
                isRestoringState = false
                // Also clear sync flag after restoration
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    isSyncingTabContent = false

                    // CRITICAL: Trigger textStore subscription by updating it
                    // This ensures any pending updates are processed now that the flag is cleared
                    const currentState = get(textStore)
                    textStore.set(currentState)
                  })
                })
              }, 50)
            }, 50)
          } else {
            // No inline views to restore, reset flag sooner
            setTimeout(() => {
              isRestoringState = false
              // Also clear sync flag
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  isSyncingTabContent = false

                  // CRITICAL: Trigger textStore subscription
                  const currentState = get(textStore)
                  textStore.set(currentState)
                })
              })
            }, 100)
          }
        } else {
          // Clear state for new tab
          errorState = {
            errors: [],
            ignoredErrors: new Set(),
            appliedCorrections: new Map(),
            activeErrorId: null
          }
          inlineViews = []
          
          // Clear stores after a delay
          setTimeout(() => {
            if (!isRestoringState) return
            errorStore.set(errorState)
            inlineViewStore.clearAll()
            
            // Reset restoration flag after clearing is complete
            setTimeout(() => {
              isRestoringState = false
              // Also clear sync flag
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  isSyncingTabContent = false

                  // CRITICAL: Trigger textStore subscription
                  const currentState = get(textStore)
                  textStore.set(currentState)
                })
              })
            }, 50)
          }, 50)
        }
      }

      // Always update previous tab ID (even for file renames)
      if (tab?.id) {
        previousTabId = tab.id
      } else if (!tab) {
        // No tab selected
        previousTabId = null
      }
    })
    return unsubscribe
  })
  
  // Save check state when grammar check completes
  function saveCurrentTabCheckState() {
    if (currentTab?.id && !isRestoringState) {
      fileStore.saveTabCheckState(currentTab.id, errorState, inlineViews)
    }
  }
  
  // Debounced save for automatic state persistence
  let saveTimeout: ReturnType<typeof setTimeout> | null = null
  function debouncedSaveState() {
    if (!currentTab?.id || isRestoringState) return
    
    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }
    
    saveTimeout = setTimeout(() => {
      saveCurrentTabCheckState()
      saveTimeout = null
    }, 1000) // Save after 1 second of inactivity
  }
  
  // Subscribe to hasUnsavedChanges
  $effect(() => {
    const unsubscribe = hasUnsavedChanges.subscribe(value => {
      hasUnsaved = value
    })
    return unsubscribe
  })
  let saveStatus = $state<'idle' | 'saving' | 'saved' | 'error'>('idle')
  let lastSaved = $state<Date | null>(null)
  let explorerMode = $state<'explorer' | 'search'>('explorer')
  let documentTitle = $state('')
  let searchQuery = $state('')
  
  // Explorer settings - load from persistent store
  const explorerSettings = createSvelteStore(explorerSettingsStore)
  let showFileExplorer = $state(explorerSettingsStore.get().isOpen)
  let explorerWidth = $state(explorerSettingsStore.get().width)
  let isResizingExplorer = $state(false)
  let explorerResizeRef = $state<HTMLDivElement | null>(null)
  
  // Subscribe to explorer settings changes
  $effect(() => {
    const unsubscribe = explorerSettings.subscribe(settings => {
      showFileExplorer = settings.isOpen
      explorerWidth = settings.width
    })
    return unsubscribe
  })
  
  // Use drag hook for explorer resize
  useDrag(
    () => explorerResizeRef,
    {
      onDragStart: (e) => {
        e.preventDefault()
        isResizingExplorer = true
      },
      onDrag: (e) => {
        const newWidth = e.clientX - 40 // Subtract toolbar width
        // Limit width between 180px and 480px
        if (newWidth >= 180 && newWidth <= 480) {
          explorerWidth = newWidth
        }
      },
      onDragEnd: () => {
        isResizingExplorer = false
        // Save the new width to persistent storage
        updateExplorerSettings({ width: explorerWidth })
      },
      cursor: 'col-resize'
    }
  )
  
  // Watch textareaElement changes (currently unused)
  // $effect(() => {
  //   mainEditorTextareaStore.set(textareaElement)
  // })
  
  // Update sidebar content state
  $effect(() => {
    // Only count active inline views (not applied or cancelled)
    const activeInlineViews = inlineViews.filter(
      v => v.status !== 'applied' && v.status !== 'cancelled'
    )
    
    sidebarStore.updateContentState({
      hasErrors: errorState.errors.length > 0,
      hasInline: activeInlineViews.length > 0,
      hasAI: showSplitEditor,
      hasTips: false // Will be updated when tips are shown
    })
  })
  
  // Keyboard shortcut for sidebar toggle
  useKeyboard('cmd+shift+/', () => {
    sidebarStore.toggle()
  })
  
  // Keyboard shortcuts for file operations
  useKeyboard('cmd+/', (e) => {
    e.preventDefault()
    toggleFileExplorer()
  })
  
  useKeyboard('cmd+s', async (e) => {
    e.preventDefault()
    await saveCurrentTab()
  })
  
  useKeyboard('cmd+shift+s', async (e) => {
    e.preventDefault()
    // Save all tabs if needed
    const state = get(fileStore)
    for (const tab of state.tabs.values()) {
      if (tab.isDirty) {
        await fileStore.saveTab(tab.id)
      }
    }
  })
  
  useKeyboard('cmd+n', async (e) => {
    e.preventDefault()
    await createNewDocument()
  })
  
  // Fullscreen shortcut (F11)
  useKeyboard('f11', async (e) => {
    e.preventDefault()
    await toggleFullscreen()
  })
  
  // Restore selection visually when generation bar is shown
  $effect(() => {
    if (showGenerationPrompt && textareaElement) {
      const startAttr = textareaElement.getAttribute('data-selection-start')
      const endAttr = textareaElement.getAttribute('data-selection-end')
      
      if (startAttr && endAttr) {
        const start = parseInt(startAttr)
        const end = parseInt(endAttr)
        
        // Restore the selection visually
        requestAnimationFrame(() => {
          textareaElement.setSelectionRange(start, end)
        })
      }
    }
  })
  
  // Subscribe to search store
  $effect(() => {
    const unsubscribe = searchStore.subscribe(state => {
      searchState = state
      // Don't sync searchTerm from store - let SearchBar manage it
    })
    return unsubscribe
  })
  
  // Subscribe to view mode changes
  $effect(() => {
    const unsubscribe = currentViewMode.subscribe(mode => {
      // Only clear state if mode actually changed
      if (viewMode !== mode) {
        const previousMode = viewMode
        viewMode = mode
        
        // Clear inline views and highlighting when switching modes
        inlineViewStore.clearAll()
        
        // Clear selection highlight in TipTap if exists
        if (tipTapEditor) {
          tipTapEditor.clearSelectionHighlight()
        }
        
        // Clear generation prompt if open
        if (showGenerationPrompt) {
          showGenerationPrompt = false
          showSelectionOverlay = false
          selectedTextForGeneration = ''
          generationSelectionRange = null
          generationBarHeight = 0
        }
        
        // Clear textarea selection attributes
        if (textareaElement) {
          textareaElement.removeAttribute('data-has-selection')
          textareaElement.removeAttribute('data-selection-start')
          textareaElement.removeAttribute('data-selection-end')
        }
        
        // When switching to WYSIWYG, sync the current markdown content
        if (mode === 'wysiwyg' && editorText) {
          editorViewStore.setMarkdownContent(editorText, false)
        }
      }
    })
    return unsubscribe
  })
  
  // Update view mode store when active tab changes
  $effect(() => {
    if (activeTab) {
      editorViewStore.setActiveTab(activeTab)
    }
  })
  
  // Derive inline view cards for sidebar display
  let inlineViewCards = $derived(
    inlineViews.filter(v => v.type === 'card' && v.status !== 'applied')
  )
  
  // Controller references
  let aiController: AIFeatureController
  let clipboardController: ClipboardController
  let correctionController: CorrectionController
  let grammarCheckController: GrammarCheckController
  let textEditingController: TextEditingController
  // svelte-ignore non_reactive_update
  let inlineViewController: InlineViewController
  
  // Expose inlineViewController to window for AIFeatureController
  $effect(() => {
    if (inlineViewController) {
      (window as any).inlineViewController = inlineViewController
    }
  })
  
  // Subscribe to store updates
  $effect(() => {
    const unsubscribeText = textStore.subscribe(value => {
      textState = value
      // Sync editor text when store changes (e.g., from upgrade prompt)
      // But NOT while user is actively typing OR during state restoration
      // FIXED: Improved isUserTyping check to include TipTap editor (ProseMirror)
      const isUserTyping = document.activeElement?.tagName === 'TEXTAREA' ||
                           document.activeElement?.classList?.contains('ProseMirror') ||
                           tipTapEditor?.isFocused

      // FIXED: Skip sync during state restoration to prevent race conditions
      if (isRestoringState) return

      if (value.current !== editorText && value.current !== undefined && !isUserTyping) {
        editorText = value.current
        // Also update TextManager if it exists
        if (textManager) {
          textManager.setText(value.current)
        }
      }
    })
    const unsubscribeError = errorStore.subscribe(value => {
      // Skip updates during state restoration
      if (isRestoringState) return
      
      errorState = value
      // Save state in a debounced way
      debouncedSaveState()
      
      // Don't reset allCorrectionsApplied when errors are cleared after applying all
      // Only reset when new errors appear
      if (value.errors.length > 0 && allCorrectionsApplied) {
        allCorrectionsApplied = false
      }
    })
    const unsubscribeStats = statsStore.subscribe(value => stats = value)
    const unsubscribeSettings = settingsStore.subscribe(value => settings = value)
    const unsubscribeSubscription = subscriptionStore.subscribe(value => subscription = value)
    const unsubscribeAI = aiFeaturesStore.subscribe(value => {
      if (value.segments) {
        aiSegments = value.segments
      }
    })
    const unsubscribeInline = inlineViewStore.subscribe(value => {
      // Skip updates during state restoration
      if (isRestoringState) return
      
      inlineViews = value.views
      // Save state in a debounced way
      debouncedSaveState()
    })
    const unsubscribeUnifiedPrompt = unifiedPromptStore.subscribe(value => {
      unifiedPromptState = value
    })
    
    // Register AIService status callback
    import('$lib/services/AIService').then(({ AIService }) => {
      AIService.setStatusCallback((message, type) => {
        showStatus(message, 5000, type)
      })
    })
    
    return () => {
      unsubscribeText()
      unsubscribeError()
      unsubscribeStats()
      unsubscribeSettings()
      unsubscribeSubscription()
      unsubscribeAI()
      unsubscribeInline()
      unsubscribeUnifiedPrompt()
    }
  })
  
  // Status message auto-hide timer
  const statusTimer = useAutoHide(3500, () => {
    statusMessage = null
  })
  
  // Clean up inline views when text changes
  let previousEditorText = $state<string>('')
  $effect(() => {
    // Skip cleanup during state restoration to preserve inline views when switching tabs
    if (isRestoringState) return
    
    // Only check if text actually changed
    if (editorText !== previousEditorText) {
      inlineViewStore.cleanupInvalidViews(editorText)
      previousEditorText = editorText
      // Clear lastSelection when text changes to prevent stale selections
      lastSelection = null
    }
  })
  
  // Show status message temporarily
  function showStatus(message: string, duration: number | 'persist' = 3500, type?: 'error' | 'info' | 'success' | 'warning') {
    statusMessage = message
    if (duration === 'persist' || isAIProcessing) {
      // Don't auto-hide during AI processing or when persist is specified
      statusTimer.hide()
    } else if (duration !== 3500) {
      statusTimer.extend(duration)
    } else {
      statusTimer.show()
    }
  }
  
  // Re-check selection whenever textareaElement changes
  $effect(() => {
    if (textareaElement && viewMode === 'source') {
      // Check selection immediately when textarea element changes
      const hasSelection = textareaElement.selectionStart !== textareaElement.selectionEnd
      hasTextSelection = hasSelection
      
      if (hasSelection) {
        const start = textareaElement.selectionStart
        const end = textareaElement.selectionEnd
        const text = editorText.substring(start, end)
        lastSelection = { start, end, text }
      }
    }
  })
  
  // Check if there's a text selection
  $effect(() => {
    // Force reactivity by accessing textareaElement at the beginning
    const currentTextarea = textareaElement
    
    // Debounce timer for selection checks
    let selectionDebounceTimer: ReturnType<typeof setTimeout> | null = null
    
    const checkSelection = () => {
      // Check if we're in WYSIWYG mode
      if (viewMode === 'wysiwyg' && tipTapEditor) {
        const selection = tipTapEditor.getSelection()
        if (selection && selection.length > 0) {
          hasTextSelection = true
          // Get selection positions from TipTap
          // Note: TipTap positions are for content without YAML
          const editor = (tipTapEditor as any).editor
          if (editor) {
            const { from, to } = editor.state.selection
            lastSelection = { start: from, end: to, text: selection }
            
            // Clear lastSelection after 3 seconds to prevent stale selections
            if (lastSelectionTimer) {
              clearTimeout(lastSelectionTimer)
            }
            lastSelectionTimer = setTimeout(() => {
              lastSelection = null
              hasTextSelection = false
            }, 3000)
          }
        } else {
          hasTextSelection = false
          lastSelection = null
          if (lastSelectionTimer) {
            clearTimeout(lastSelectionTimer)
            lastSelectionTimer = null
          }
        }
      } else {
        // Original textarea logic - use textareaElement directly
        const textarea = textareaElement
        if (textarea) {
          const hasSelection = textarea.selectionStart !== textarea.selectionEnd
          hasTextSelection = hasSelection
          // Store selection info for AI features
          if (hasSelection) {
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            const text = editorText.substring(start, end)
            lastSelection = { start, end, text }
            
            // Clear lastSelection after 3 seconds to prevent stale selections
            if (lastSelectionTimer) {
              clearTimeout(lastSelectionTimer)
            }
            lastSelectionTimer = setTimeout(() => {
              lastSelection = null
              lastSelectionTimer = null
            }, 3000)
          } else {
            // Clear lastSelection when no selection exists
            lastSelection = null
            if (lastSelectionTimer) {
              clearTimeout(lastSelectionTimer)
              lastSelectionTimer = null
            }
          }
        }
      }
    }
    
    // Debounced version for frequent events
    const checkSelectionDebounced = () => {
      if (selectionDebounceTimer) {
        clearTimeout(selectionDebounceTimer)
      }
      selectionDebounceTimer = setTimeout(checkSelection, 50)
    }
    
    // Store selection immediately on select event (no debounce)
    const handleSelect = (e: Event) => {
      if (e.target instanceof HTMLTextAreaElement) {
        const hasSelection = e.target.selectionStart !== e.target.selectionEnd
        if (hasSelection) {
          const start = e.target.selectionStart
          const end = e.target.selectionEnd
          const text = editorText.substring(start, end)
          lastSelection = { start, end, text }
          
          // Clear lastSelection after 3 seconds to prevent stale selections
          if (lastSelectionTimer) {
            clearTimeout(lastSelectionTimer)
          }
          lastSelectionTimer = setTimeout(() => {
            lastSelection = null
            lastSelectionTimer = null
          }, 3000)
        } else {
          // Clear lastSelection when selection is cleared
          lastSelection = null
          if (lastSelectionTimer) {
            clearTimeout(lastSelectionTimer)
            lastSelectionTimer = null
          }
        }
      }
    }
    
    // Store selection before shortcuts are triggered (no debounce)
    const handleKeyDown = (e: KeyboardEvent) => {
      const modifierKey = navigator.platform.includes('Mac') ? e.metaKey : e.ctrlKey
      if (modifierKey && e.shiftKey) {
        // Store selection immediately before any AI shortcuts
        checkSelection()
      }
    }
    
    // Expose checkSelection for TipTap callback
    ;(window as any).checkSelectionForTipTap = checkSelection
    
    // Add textarea-specific listeners if textarea exists
    if (currentTextarea && viewMode === 'source') {
      currentTextarea.addEventListener('select', checkSelection)
      currentTextarea.addEventListener('mouseup', checkSelection)
      currentTextarea.addEventListener('keyup', checkSelection)
    }
    
    // Use debounced version for frequent events
    document.addEventListener('selectionchange', checkSelectionDebounced)
    document.addEventListener('mouseup', checkSelectionDebounced)
    document.addEventListener('keyup', checkSelectionDebounced)
    // Use immediate version for critical events
    document.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    document.addEventListener('select', handleSelect, true) // Use capture phase
    
    return () => {
      if (selectionDebounceTimer) {
        clearTimeout(selectionDebounceTimer)
      }
      
      // Remove textarea-specific listeners if they were added
      if (currentTextarea && viewMode === 'source') {
        currentTextarea.removeEventListener('select', checkSelection)
        currentTextarea.removeEventListener('mouseup', checkSelection)
        currentTextarea.removeEventListener('keyup', checkSelection)
      }
      
      document.removeEventListener('selectionchange', checkSelectionDebounced)
      document.removeEventListener('mouseup', checkSelectionDebounced)
      document.removeEventListener('keyup', checkSelectionDebounced)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('select', handleSelect, true)
      delete (window as any).checkSelectionForTipTap
    }
  })
  
  // Controller delegation methods
  async function handleCheckGrammar() {
    // Reset allCorrectionsApplied when doing a new grammar check
    if (allCorrectionsApplied) {
      allCorrectionsApplied = false
      processedErrorCount = 0
    }

    // If in reading mode (wysiwyg), switch to source mode temporarily
    if (viewMode === 'wysiwyg') {
      originalViewModeForGrammarCheck = viewMode
      viewMode = 'source'
      editorViewStore.setMode('source')

      // Small delay to ensure mode switch completes
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    await grammarCheckController?.handleCheckGrammar()
    // Save check state after grammar check completes
    saveCurrentTabCheckState()
  }
  
  async function handleAIAnalysis() {
    if (!aiController) {
      console.error('[MainEditor] aiController not available')
      return
    }
    await aiController.handleAIAnalysis()
    // Save check state after AI analysis completes
    saveCurrentTabCheckState()
  }
  
  async function handleParaphrase() {
    // Check if there's a selection (use lastSelection if current selection is lost)
    let start: number, end: number, selectedText: string

    // In WYSIWYG mode, use TipTap selection
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      // Try to get markdown selection first, fallback to plain text
      const markdownSelection = tipTapEditor.getSelectionAsMarkdown?.()
      const plainSelection = tipTapEditor.getSelection()


      if ((markdownSelection || plainSelection) && (markdownSelection?.length > 0 || plainSelection?.length > 0)) {
        const editor = (tipTapEditor as any).editor
        if (editor) {
          const { from, to } = editor.state.selection
          // Use TipTap native coordinates without YAML offset conversion
          // This avoids coordinate mismatch issues with InlineViewController
          start = from
          end = to
          // Use markdown selection if available to preserve formatting
          selectedText = markdownSelection || plainSelection
          
        } else if (lastSelection) {
          start = lastSelection.start
          end = lastSelection.end
          selectedText = lastSelection.text
        } else {
          await aiController?.handleParaphrase(undefined, true)
          return
        }
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleParaphrase(undefined, true)
        return
      }
    } else {
      // Source mode - use bound textareaElement
      const textarea = textareaElement
      if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
        start = textarea.selectionStart
        end = textarea.selectionEnd
        selectedText = editorText.substring(start, end)
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleParaphrase(undefined, false)
        return
      }
    }
    
    // Now we have selection info, either current or stored
    // Always use inline view (card) for selected text
    // This ensures all selections are processed properly regardless of length
    await inlineViewController?.processSelection(
      selectedText,
      start,
      end,
      'paraphrase',
      null // No need to pass textarea element
    )
    // Open sidebar to show inline AI results
    sidebarStore.open()
  }
  
  async function handleExpand() {
    // Check if there's a selection (use lastSelection if current selection is lost)
    let start: number, end: number, selectedText: string
    
    // In WYSIWYG mode, use TipTap selection
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      // Try to get markdown selection first, fallback to plain text
      const markdownSelection = tipTapEditor.getSelectionAsMarkdown?.()
      const plainSelection = tipTapEditor.getSelection()

      if ((markdownSelection || plainSelection) && (markdownSelection?.length > 0 || plainSelection?.length > 0)) {
        const editor = (tipTapEditor as any).editor
        if (editor) {
          const { from, to } = editor.state.selection
          // Use TipTap native coordinates without YAML offset conversion
          // This avoids coordinate mismatch issues with InlineViewController
          start = from
          end = to
          // Use markdown selection if available to preserve formatting
          selectedText = markdownSelection || plainSelection
        } else if (lastSelection) {
          start = lastSelection.start
          end = lastSelection.end
          selectedText = lastSelection.text
        } else {
          await aiController?.handleExpand(undefined, true)
          return
        }
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleExpand(undefined, viewMode === 'wysiwyg')
        return
      }
    } else {
      // Source mode - use bound textareaElement
      const textarea = textareaElement
      if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
        start = textarea.selectionStart
        end = textarea.selectionEnd
        selectedText = editorText.substring(start, end)
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleExpand(undefined, viewMode === 'wysiwyg')
        return
      }
    }
      
    // Always use inline view (card) for selected text
    await inlineViewController?.processSelection(
      selectedText,
      start,
      end,
      'expand',
      null // No need to pass textarea element
    )
    // Open sidebar to show inline AI results
    sidebarStore.open()
  }
  
  async function handleShrink() {
    // Check if there's a selection (use lastSelection if current selection is lost)
    let start: number, end: number, selectedText: string
    
    // In WYSIWYG mode, use TipTap selection
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      // Try to get markdown selection first, fallback to plain text
      const markdownSelection = tipTapEditor.getSelectionAsMarkdown?.()
      const plainSelection = tipTapEditor.getSelection()

      if ((markdownSelection || plainSelection) && (markdownSelection?.length > 0 || plainSelection?.length > 0)) {
        const editor = (tipTapEditor as any).editor
        if (editor) {
          const { from, to } = editor.state.selection
          // Use TipTap native coordinates without YAML offset conversion
          // This avoids coordinate mismatch issues with InlineViewController
          start = from
          end = to
          // Use markdown selection if available to preserve formatting
          selectedText = markdownSelection || plainSelection
        } else if (lastSelection) {
          start = lastSelection.start
          end = lastSelection.end
          selectedText = lastSelection.text
        } else {
          await aiController?.handleShrink(undefined, true)
          return
        }
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleShrink(undefined, viewMode === 'wysiwyg')
        return
      }
    } else {
      // Source mode - use bound textareaElement
      const textarea = textareaElement
      if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
        start = textarea.selectionStart
        end = textarea.selectionEnd
        selectedText = editorText.substring(start, end)
      } else if (lastSelection) {
        start = lastSelection.start
        end = lastSelection.end
        selectedText = lastSelection.text
      } else {
        await aiController?.handleShrink(undefined, viewMode === 'wysiwyg')
        return
      }
    }
      
    // Always use inline view (card) for selected text
    await inlineViewController?.processSelection(
      selectedText,
      start,
      end,
      'shrink',
      null // No need to pass textarea element
    )
    // Open sidebar to show inline AI results
    sidebarStore.open()
  }
  
  async function handleContinue(mode: 'sentence' | 'paragraph' = 'sentence') {
    // Handle selection differently for WYSIWYG vs source mode
    let start: number, end: number, selectedText: string | null = null
    let cursorPosition: number = 0
    
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      const selection = tipTapEditor.getSelection()
      const editor = (tipTapEditor as any).editor
      if (editor) {
        const { from, to } = editor.state.selection
        // Use TipTap native coordinates without YAML offset conversion
        // This avoids coordinate mismatch issues with InlineViewController
        cursorPosition = from
        if (selection && selection.length > 0) {
          start = from
          end = to
          selectedText = selection
        }
      }
    } else {
      const textarea = textareaElement
      if (!textarea) return
      
      cursorPosition = textarea.selectionStart
      
      // Check if cursor is inside or at the edge of an active inline view
      const activeInlineViews = get(inlineViewStore).views.filter(v => v.status !== 'applied' && v.status !== 'cancelled')
      const cursorInView = activeInlineViews.find(v => 
        cursorPosition >= v.position.start && cursorPosition <= v.position.end
      )
      
      if (cursorInView) {
        // Cursor is inside or at the edge of a highlighted view
        // Move cursor outside the view to allow normal continuation
        const newPosition = cursorInView.position.end + 1
        textarea.setSelectionRange(newPosition, newPosition)
        textarea.focus()
        
        // Small delay to ensure cursor position is updated
        await new Promise(resolve => setTimeout(resolve, 10))
      }
      
      // Now check for selection
      if (textarea.selectionStart !== textarea.selectionEnd) {
        start = textarea.selectionStart
        end = textarea.selectionEnd
        selectedText = editorText.substring(start, end)
      }
    }
    
    // Process selection if exists
    if (selectedText && start !== undefined && end !== undefined) {
      // Always use inline view (card) for selected text
      await inlineViewController?.processSelection(
        selectedText,
        start,
        end,
        'continue',
        null // No need to pass textarea element
      )
      // Open sidebar to show inline AI results
      sidebarStore.open()
    } else {
      // No selection - do continuation at cursor position
      await aiController?.handleContinue(mode)
    }
  }
  
  async function handleSummarize() {
    const isWysiwyg = viewMode === 'wysiwyg'
    await aiController?.handleSummarize(isWysiwyg)
  }
  
  async function handleCritic() {
    await aiController?.handleCritic()
  }
  
  async function handleGenerate(prompt: string, model: string, mode?: 'sentence' | 'paragraph') {
    showGenerationPrompt = false
    showSelectionOverlay = false
    generationBarHeight = 0
    
    // Clear the selection highlight when closing the prompt
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      tipTapEditor.clearSelectionHighlight()
    }
    
    // Handle WYSIWYG mode
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      const editor = (tipTapEditor as any).editor
      if (!editor) return
      
      // Check if there was selected text or cursor position saved
      if (generationSelectionRange) {
        if (selectedTextForGeneration) {
          // Has selection - process as modification
          await inlineViewController?.processSelectionWithPrompt(
            selectedTextForGeneration,
            generationSelectionRange.start,
            generationSelectionRange.end,
            prompt,
            model,
            mode
          )
          
          // Open sidebar to show inline view results (only for modifications)
          sidebarStore.open()
        } else {
          // No selection - generate at cursor position
          await inlineViewController?.processGeneration(
            prompt,
            generationSelectionRange.start,
            model,
            mode
          )
          // Don't open sidebar for direct generation since there are no inline views
        }
      }
    } else {
      // Source mode - use textarea
      const textarea = textareaElement
      if (!textarea) return
      
      // Get saved selection if any
      const savedStart = textarea.getAttribute('data-selection-start')
      const savedEnd = textarea.getAttribute('data-selection-end')
      
      // Clear text selection highlight
      textarea.removeAttribute('data-has-selection')
      textarea.removeAttribute('data-selection-start')
      textarea.removeAttribute('data-selection-end')
      
      // Ensure textarea has focus and is ready before processing
      textarea.focus()
      
      // Small delay to ensure DOM updates and focus is established
      await new Promise(resolve => setTimeout(resolve, 50))
      
      // Check if there was selected text
      if (savedStart && savedEnd && selectedTextForGeneration) {
        const start = parseInt(savedStart)
        const end = parseInt(savedEnd)
        
        // Process as modification of selected text
        await inlineViewController?.processSelectionWithPrompt(
          selectedTextForGeneration,
          start,
          end,
          prompt,
          model,
          mode
        )
        
        // Open sidebar to show inline view results (only for modifications)
        sidebarStore.open()
      } else {
        // Generate at cursor position - text is directly inserted, no inline views
        const cursorPos = textarea.selectionStart
        await inlineViewController?.processGeneration(
          prompt,
          cursorPos,
          model,
          mode
        )
        // Don't open sidebar for direct generation since there are no inline views
      }
    }
    
    // Clear selected text
    selectedTextForGeneration = ''
    generationSelectionRange = null
  }
  
  function handleShowGenerationPrompt() {
    // Toggle functionality - close if already open
    if (showGenerationPrompt) {
      showGenerationPrompt = false
      showSelectionOverlay = false
      selectedTextForGeneration = ''
      generationSelectionRange = null
      generationBarHeight = 0
      // Clear the selection highlight when closing the prompt
      if (viewMode === 'wysiwyg' && tipTapEditor) {
        tipTapEditor.clearSelectionHighlight()
      }
      const textarea = textareaElement
      if (textarea) {
        textarea.removeAttribute('data-has-selection')
        textarea.removeAttribute('data-selection-start')
        textarea.removeAttribute('data-selection-end')
      }
      return
    }
    
    showGenerationPrompt = true
    
    // Handle WYSIWYG mode
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      const editor = (tipTapEditor as any).editor
      if (editor) {
        const { from, to } = editor.state.selection

        if (from !== to) {
          // Has selection - get text as markdown from TipTap editor to preserve formatting
          selectedTextForGeneration = tipTapEditor.getSelectionAsMarkdown() || editor.state.doc.textBetween(from, to)
          // Use TipTap native coordinates without YAML offset conversion
          // This avoids coordinate mismatch issues with InlineViewController
          generationSelectionRange = { start: from, end: to }
          
          // Highlight the selection in TipTap editor
          tipTapEditor.highlightSelection(from, to)
        } else {
          // No selection - will use cursor position for insertion
          selectedTextForGeneration = ''
          // Use TipTap native coordinates without YAML offset conversion
          generationSelectionRange = { start: from, end: from }
        }
      }
    } else {
      // Source mode - use textarea
      const textarea = textareaElement
      if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
        // Has selection - use selected text
        selectedTextForGeneration = editorText.substring(textarea.selectionStart, textarea.selectionEnd)
        generationSelectionRange = { start: textarea.selectionStart, end: textarea.selectionEnd }
        textarea.setAttribute('data-has-selection', 'true')
        textarea.setAttribute('data-selection-start', String(textarea.selectionStart))
        textarea.setAttribute('data-selection-end', String(textarea.selectionEnd))
        // Show selection overlay
        showSelectionOverlay = true
      } else {
        // No selection - set empty string to trigger "무엇을 생성할까요?" placeholder
        selectedTextForGeneration = ''
        generationSelectionRange = null
        textarea?.removeAttribute('data-has-selection')
        textarea?.removeAttribute('data-selection-start')
        textarea?.removeAttribute('data-selection-end')
        showSelectionOverlay = false
      }
    }
  }
  
  function handleClearText() {
    clipboardController?.handleClearText()
  }
  
  async function handleCopyText() {
    await clipboardController?.handleCopyText()
  }
  
  async function handleCutText() {
    await clipboardController?.handleCutText()
  }
  
  function handleExportPDF() {
    if (currentTab && editorText) {
      showPDFExport = true
    }
  }
  
  async function toggleFullscreen() {
    const isElectron = typeof window !== 'undefined' && window.api
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

    // Mobile web: Open in new tab instead of fullscreen
    if (!isElectron && isMobile) {
      const isDemoMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true'
      const url = isDemoMode
        ? `${window.location.origin}?demo=true&hideExplorer=true`
        : window.location.origin
      window.open(url, '_blank')
      return
    }

    if (isElectron) {
      // Electron: Use native fullscreen
      if (window.api?.toggleFullscreen) {
        isFullscreen = await window.api.toggleFullscreen()
      }
    } else {
      // Web browser: Use Fullscreen API
      if (!document.fullscreenElement) {
        // Enter fullscreen
        try {
          const docElem = document.documentElement

          if (docElem.requestFullscreen) {
            await docElem.requestFullscreen()
          } else if ((docElem as any).webkitRequestFullscreen) {
            await (docElem as any).webkitRequestFullscreen()
          } else if ((docElem as any).mozRequestFullScreen) {
            await (docElem as any).mozRequestFullScreen()
          } else if ((docElem as any).msRequestFullscreen) {
            await (docElem as any).msRequestFullscreen()
          }

          isFullscreen = true
        } catch (error) {
          console.error('Failed to enter fullscreen:', error)
          // Fallback: programmatically trigger F11
          const event = new KeyboardEvent('keydown', {
            key: 'F11',
            code: 'F11',
            keyCode: 122,
            bubbles: true
          })
          document.dispatchEvent(event)
        }
      } else {
        // Exit fullscreen
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if ((document as any).webkitExitFullscreen) {
            await (document as any).webkitExitFullscreen()
          } else if ((document as any).mozCancelFullScreen) {
            await (document as any).mozCancelFullScreen()
          } else if ((document as any).msExitFullscreen) {
            await (document as any).msExitFullscreen()
          }

          isFullscreen = false
        } catch (error) {
          console.error('Failed to exit fullscreen:', error)
          // Fallback: programmatically trigger F11
          const event = new KeyboardEvent('keydown', {
            key: 'F11',
            code: 'F11',
            keyCode: 122,
            bubbles: true
          })
          document.dispatchEvent(event)
        }
      }
    }
  }
  
  // Listen for browser fullscreen state changes
  onMount(() => {
    const handleFullscreenChange = () => {
      isFullscreen = !!document.fullscreenElement
    }
    
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  })
  
  async function handlePasteText() {
    await clipboardController?.handlePasteText()
  }
  
  // Helper function to restore original view mode after grammar check completes
  function restoreOriginalViewMode() {
    if (originalViewModeForGrammarCheck !== null) {
      // Restore the original view mode
      viewMode = originalViewModeForGrammarCheck
      editorViewStore.setMode(originalViewModeForGrammarCheck)
      originalViewModeForGrammarCheck = null
    }
  }
  
  function handleApplyCorrection(errorId: string, correctionId: string) {
    // Track if this is the last error being corrected
    const beforeCorrectionActiveCount = errorState.errors.filter(e => 
      !errorState.ignoredErrors.has(e.id) && 
      e.userAction !== 'kept-original' && 
      !e.isOriginalKept
    ).length
    
    correctionController?.handleApplyCorrection(errorId, correctionId)
    
    // Check if all errors are processed after individual correction
    setTimeout(() => {
      const activeErrors = errorState.errors.filter(e => 
        !errorState.ignoredErrors.has(e.id) && 
        e.userAction !== 'kept-original' && 
        !e.isOriginalKept
      )
      
      // If we had errors before and now have none, all have been corrected
      if (beforeCorrectionActiveCount > 0 && activeErrors.length === 0 && hasChecked && initialErrorCount > 0) {
        if (!allCorrectionsApplied) {
          allCorrectionsApplied = true
          processedErrorCount = initialErrorCount
          // Restore original view mode when all corrections are applied
          restoreOriginalViewMode()
        }
      }
    }, 50)
  }
  
  function handleApplyAlternative(errorId: string, alternativeText: string) {
    // Track if this is the last error being corrected
    const beforeCorrectionActiveCount = errorState.errors.filter(e => 
      !errorState.ignoredErrors.has(e.id) && 
      e.userAction !== 'kept-original' && 
      !e.isOriginalKept
    ).length
    
    correctionController?.handleApplyAlternative(errorId, alternativeText)
    
    // Check if all errors are processed after alternative correction
    setTimeout(() => {
      const activeErrors = errorState.errors.filter(e => 
        !errorState.ignoredErrors.has(e.id) && 
        e.userAction !== 'kept-original' && 
        !e.isOriginalKept
      )
      
      // If we had errors before and now have none, all have been corrected
      if (beforeCorrectionActiveCount > 0 && activeErrors.length === 0 && hasChecked && initialErrorCount > 0) {
        if (!allCorrectionsApplied) {
          allCorrectionsApplied = true
          processedErrorCount = initialErrorCount
          // Restore original view mode when all corrections are applied
          restoreOriginalViewMode()
        }
      }
    }, 50)
  }
  
  function handleApplyAllCorrections() {
    // Store the current error count before applying
    const currentErrorCount = errorState.errors.length
    correctionController?.handleApplyAllCorrections()
    // The callback will be triggered by the controller
  }
  
  function handleRejectAllCorrections() {
    correctionController?.handleRejectAllCorrections()
    // Restore original view mode when all corrections are rejected
    restoreOriginalViewMode()
  }
  
  function handleIgnoreError(errorId: string) {
    correctionController?.handleIgnoreError(errorId)
    
    // Check if all errors are ignored after individual ignore
    setTimeout(() => {
      const activeErrors = errorState.errors.filter(e => 
        !errorState.ignoredErrors.has(e.id) && 
        e.userAction !== 'kept-original' && 
        !e.isOriginalKept
      )
      
      // Check if all initial errors have been ignored
      if (activeErrors.length === 0 && errorState.errors.length > 0 && hasChecked) {
        // All remaining errors have been ignored
        if (errorState.ignoredErrors.size >= errorState.errors.length) {
          // All errors have been ignored, not applied
          allCorrectionsApplied = false
          processedErrorCount = 0
          // Restore original view mode when all errors are ignored
          restoreOriginalViewMode()
        }
      }
    }, 50)
  }
  
  function handleKeepOriginal(errorId: string) {
    correctionController?.handleKeepOriginal(errorId)
  }
  
  function handleOpenSettings() {
    // Dispatch event to open settings modal
    window.dispatchEvent(new CustomEvent('open-settings'))
  }
  
  // Export methods for theme toggle
  export function toggleTheme() {
    editorHeader?.toggleTheme?.()
  }
  
  // Reactive search with optimized text change detection
  // This ensures search highlights stay accurate when text is modified
  $effect(() => {
    // Skip if search is not visible or no search term
    if (!searchState.isVisible || !searchTerm) {
      // Clear last search tracking when search is hidden
      if (!searchState.isVisible) {
        lastSearchedText = ''
        lastSearchTerm = ''
      }
      return
    }
    
    // Check if text has changed since last search
    const textChanged = editorText !== lastSearchedText
    const termChanged = searchTerm !== lastSearchTerm
    
    if (textChanged || termChanged) {
      // Clear existing timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
      
      // Calculate text change size for smarter debouncing
      const isSmallChange = Math.abs(editorText.length - lastSearchedText.length) <= 1
      
      // Use different delays based on change type:
      // - Search term change: 20ms (very responsive)
      // - Small text change (typing): 10ms (nearly instant)
      // - Large text change (paste/delete): 50ms (still fast)
      let debounceDelay: number
      if (termChanged && !textChanged) {
        debounceDelay = 20  // User typing in search box
      } else if (textChanged && isSmallChange) {
        debounceDelay = 10  // Single character edit - almost instant
      } else {
        debounceDelay = 50  // Large paste or deletion
      }
      
      // Debounce search to avoid excessive recalculation
      searchDebounceTimer = setTimeout(() => {
        // Only re-search if text or term actually changed
        if (textChanged || termChanged) {
          performSearch(searchTerm, editorText)
          lastSearchedText = editorText
          lastSearchTerm = searchTerm
        }
        searchDebounceTimer = null
      }, debounceDelay)
    }
  })
  
  // Cleanup search debounce timer
  onDestroy(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
  })
  
  // Perform search with memoization
  function performSearch(term: string, text: string) {
    if (!term) {
      searchStore.clear()
      return
    }
    
    // Search in the provided text
    const matches = searchStore.search(text, term)
    
    // Update current match index if needed
    // Try to maintain the same match position when text changes
    const currentMatch = searchStore.getCurrentMatch()
    if (currentMatch && matches.length > 0) {
      // Find the closest match to the previous position
      let closestMatchIndex = 0
      let minDistance = Infinity
      
      for (let i = 0; i < matches.length; i++) {
        const distance = Math.abs(matches[i].start - currentMatch.start)
        if (distance < minDistance) {
          minDistance = distance
          closestMatchIndex = i
        }
      }
      
      // Navigate to the closest match
      searchStore.goToMatch(closestMatchIndex)
    }
  }
  
  // Search functions
  function handleSearch(term: string) {
    searchTerm = term
    
    if (!term) {
      searchStore.clear()
      lastSearchedText = ''
      lastSearchTerm = ''
      return
    }
    
    // Perform immediate search
    performSearch(term, editorText)
    lastSearchedText = editorText
    lastSearchTerm = term
    
    // Scroll to first match immediately
    const matches = searchStore.getCurrentMatch()
    if (matches && textareaElement) {
      scrollToMatch(matches.start)
    }
  }
  
  function handleSearchNext() {
    searchStore.next()
    const currentMatch = searchStore.getCurrentMatch()
    if (currentMatch && textareaElement) {
      scrollToMatch(currentMatch.start)
    }
  }
  
  function handleSearchPrevious() {
    searchStore.previous()
    const currentMatch = searchStore.getCurrentMatch()
    if (currentMatch && textareaElement) {
      scrollToMatch(currentMatch.start)
    }
  }
  
  function handleSearchClose() {
    // Clear TipTap editor highlights if in reading mode
    if (tipTapEditor) {
      const editor = (tipTapEditor as any).editor
      if (editor) {
        editor.commands.clearSearch()
      }
    }

    if (textareaElement) {
      // Save the END position of current selection BEFORE any state changes
      const savedEndPos = textareaElement.selectionEnd
      const savedScrollPos = textareaElement.scrollTop

      // Hide search and clear tracking
      searchStore.hide()
      lastSearchedText = ''  // Reset search tracking
      lastSearchTerm = ''

      // Force focus back to textarea after search is closed
      requestAnimationFrame(() => {
        if (textareaElement) {
          // Make sure textarea gets focus
          textareaElement.focus()
          // Set cursor position
          textareaElement.setSelectionRange(savedEndPos, savedEndPos)
          textareaElement.scrollTop = savedScrollPos

          // Double-check focus is actually on textarea
          if (document.activeElement !== textareaElement) {
            textareaElement.focus()
          }
        }
      })
    } else {
      searchStore.hide()
      lastSearchedText = ''  // Reset search tracking
      lastSearchTerm = ''
    }
  }
  
  function scrollToMatch(position: number) {
    // Check if we're in wysiwyg mode (TipTap editor)
    if (viewMode === 'wysiwyg') {
      // Dispatch event for TipTap to handle scrolling
      window.dispatchEvent(new CustomEvent('scrollToCurrentMatch'))
      return
    }
    
    // Original textarea-based scrolling
    if (!textareaElement) return
    
    const match = searchStore.getCurrentMatch()
    if (!match) return
    
    // No title offset needed since we're only searching in editor text
    const actualStart = match.start
    const actualEnd = match.end
    
    if (actualStart < 0 || actualEnd > editorText.length) return
    
    // Don't focus or select text during search - highlighting is handled by CSS
    // Just scroll to the match position
    
    // Use getCaretCoordinates to get exact position for scrolling
    requestAnimationFrame(() => {
      // Get the coordinates of the match start for scrolling
      const coords = getCaretCoordinates(textareaElement, actualStart)
      const viewportHeight = textareaElement.clientHeight
      
      // Calculate the ideal scroll position to center the match
      // coords.top is the position from the top of the textarea content
      const targetScroll = coords.top - (viewportHeight / 2)
      
      // Apply the scroll
      textareaElement.scrollTop = Math.max(0, targetScroll)
      
      // Keep the search input focused if it was focused
      const searchInput = document.querySelector('.search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    })
  }
  
  // Handle split editor callbacks
  function handleSplitViewEnter(mode: string, segments: any[]) {
    splitEditorMode = mode as any
    showSplitEditor = true
    aiSegments = segments
  }
  
  function handleSplitViewExit() {
    showSplitEditor = false
    splitEditorMode = null
    aiSegments = []
  }
  
  // Initialize diff-match-patch
  const dmp = new DiffMatchPatch()
  
  function handleTextUpdate(text: string, isCorrection?: boolean, changeInfo?: { type: string; position: number; insertedLength?: number }) {
    const previousText = editorText
    editorText = text
    
    // Update search index when text changes
    if (currentTab) {
      // Create a document info object for the search index update
      const docInfo = {
        id: currentTab.id,
        title: currentTab.title,
        content: text,
        preview: text.substring(0, 200).replace(/\n/g, ' ').trim(),
        createdAt: currentTab.metadata?.createdAt || new Date(),
        updatedAt: new Date(),
        wordCount: text.length,
        tags: currentTab.metadata?.tags
      }
      fileSearchStore.updateFileIndex(docInfo)
    }
    
    // Update InlineView positions if text was inserted (e.g., from continuation)
    if (changeInfo?.type === 'insertion' && changeInfo.insertedLength) {
      const insertPos = changeInfo.position
      const insertLength = changeInfo.insertedLength
      
      // Update positions of inline views that come after the insertion point
      const currentViews = get(inlineViewStore).views
      currentViews.forEach(view => {
        if (view.status !== 'applied' && view.status !== 'cancelled') {
          // If view is after the insertion point, check if content remains the same
          if (view.position.start >= insertPos) {
            const newStart = view.position.start + insertLength
            const newEnd = view.position.end + insertLength
            const currentContent = text.substring(newStart, newEnd)
            
            // Only update position if the text content remains the same
            if (currentContent === view.originalText) {
              inlineViewStore.updateView(view.id, {
                position: {
                  ...view.position,
                  start: newStart,
                  end: newEnd
                }
              })
            } else {
              // Text changed, cancel the view
              inlineViewStore.cancelView(view.id)
            }
          }
          // If view spans across the insertion point, it gets modified - cancel it
          else if (view.position.start < insertPos && view.position.end > insertPos) {
            inlineViewStore.cancelView(view.id)
          }
        }
      })
    }
    // Check for manual text edits (when no specific changeInfo is provided)
    else if (!isCorrection && !changeInfo && previousText !== text) {
      // Use diff-match-patch to get precise changes
      const diffs = dmp.diff_main(previousText, text)
      dmp.diff_cleanupSemantic(diffs)
      
      // Build position mappings from old to new text
      let oldPos = 0
      let newPos = 0
      const positionMappings: Array<{oldStart: number; oldEnd: number; newStart: number; newEnd: number; type: 'equal' | 'insert' | 'delete' | 'replace'}> = []
      
      // Process diffs in pairs to handle replacements correctly
      for (let i = 0; i < diffs.length; i++) {
        const [op, content] = diffs[i]
        
        if (op === 0) { // No change
          positionMappings.push({
            oldStart: oldPos,
            oldEnd: oldPos + content.length,
            newStart: newPos,
            newEnd: newPos + content.length,
            type: 'equal'
          })
          oldPos += content.length
          newPos += content.length
        } else if (op === -1) { // Deletion
          // Check if next diff is an insertion (replacement)
          if (i + 1 < diffs.length && diffs[i + 1][0] === 1) {
            const insertContent = diffs[i + 1][1]
            positionMappings.push({
              oldStart: oldPos,
              oldEnd: oldPos + content.length,
              newStart: newPos,
              newEnd: newPos + insertContent.length,
              type: 'replace'
            })
            oldPos += content.length
            newPos += insertContent.length
            i++ // Skip the insertion as we've handled it
          } else {
            // Pure deletion
            positionMappings.push({
              oldStart: oldPos,
              oldEnd: oldPos + content.length,
              newStart: newPos,
              newEnd: newPos,
              type: 'delete'
            })
            oldPos += content.length
          }
        } else if (op === 1) { // Pure insertion (not part of a replacement)
          positionMappings.push({
            oldStart: oldPos,
            oldEnd: oldPos,
            newStart: newPos,
            newEnd: newPos + content.length,
            type: 'insert'
          })
          newPos += content.length
        }
      }
      
      // Update InlineView positions based on the changes
      const currentViews = get(inlineViewStore).views
      currentViews.forEach(view => {
        if (view.status !== 'applied' && view.status !== 'cancelled') {
          let shouldCancel = false
          let newStart = view.position.start
          let newEnd = view.position.end
          
          // Find all mappings that affect this view
          for (const mapping of positionMappings) {
            // View is completely before this mapping - no effect
            if (view.position.end <= mapping.oldStart) {
              continue
            }
            
            // View is completely after this mapping - shift positions
            if (view.position.start >= mapping.oldEnd) {
              const shift = (mapping.newEnd - mapping.newStart) - (mapping.oldEnd - mapping.oldStart)
              newStart += shift
              newEnd += shift
              continue
            }
            
            // View overlaps with the mapping
            if (mapping.type === 'equal') {
              // No change in this region
              continue
            } else if (mapping.type === 'insert') {
              // Check if it's purely whitespace insertion
              const insertedContent = text.substring(mapping.newStart, mapping.newEnd)
              const isOnlyWhitespace = /^\s+$/.test(insertedContent)
              
              if (isOnlyWhitespace) {
                // Whitespace insertion - adjust positions but keep the view
                if (mapping.oldStart <= view.position.start) {
                  // Insertion before or at start of view
                  const shift = mapping.newEnd - mapping.newStart
                  newStart += shift
                  newEnd += shift
                } else if (mapping.oldStart < view.position.end) {
                  // Insertion within the view - extend the end
                  const shift = mapping.newEnd - mapping.newStart
                  newEnd += shift
                }
              } else {
                // Non-whitespace insertion that affects the view
                if (mapping.oldStart > view.position.start && mapping.oldStart < view.position.end) {
                  // Insertion within the view - content changed
                  shouldCancel = true
                  break
                }
              }
            } else if (mapping.type === 'delete' || mapping.type === 'replace') {
              // Check if the deletion/replacement affects the view content
              const viewOverlapStart = Math.max(view.position.start, mapping.oldStart)
              const viewOverlapEnd = Math.min(view.position.end, mapping.oldEnd)
              
              if (viewOverlapStart < viewOverlapEnd) {
                // The view's content is being modified
                shouldCancel = true
                break
              }
            }
          }
          
          if (shouldCancel) {
            // Try to find the original text in the new text
            const searchStart = Math.max(0, newStart - 100)
            const searchEnd = Math.min(text.length, newEnd + 100)
            const foundIndex = text.indexOf(view.originalText, searchStart)
            
            if (foundIndex !== -1 && foundIndex <= searchEnd) {
              // Found the text nearby - update position
              inlineViewStore.updateView(view.id, {
                position: {
                  ...view.position,
                  start: foundIndex,
                  end: foundIndex + view.originalText.length
                }
              })
            } else {
              // Can't find the text - cancel the view
              inlineViewStore.cancelView(view.id)
            }
          } else {
            // Verify the content at the new position
            const currentContent = text.substring(newStart, newEnd)
            
            // Check if the content matches exactly
            if (currentContent === view.originalText) {
              // Update position if it changed
              if (newStart !== view.position.start || newEnd !== view.position.end) {
                inlineViewStore.updateView(view.id, {
                  position: {
                    ...view.position,
                    start: newStart,
                    end: newEnd
                  }
                })
              }
            } else {
              // Content doesn't match exactly - check if it's just whitespace differences
              const normalizedCurrent = currentContent.replace(/\s+/g, ' ').trim()
              const normalizedOriginal = view.originalText.replace(/\s+/g, ' ').trim()
              
              if (normalizedCurrent === normalizedOriginal) {
                // Same content, just whitespace differences - keep the view
                inlineViewStore.updateView(view.id, {
                  position: {
                    ...view.position,
                    start: newStart,
                    end: newEnd
                  },
                  originalText: currentContent // Update to the new whitespace format
                })
              } else {
                // Try to find the exact original text
                const foundIndex = text.indexOf(view.originalText, Math.max(0, newStart - 100))
                if (foundIndex !== -1 && Math.abs(foundIndex - newStart) < 200) {
                  inlineViewStore.updateView(view.id, {
                    position: {
                      ...view.position,
                      start: foundIndex,
                      end: foundIndex + view.originalText.length
                    }
                  })
                } else {
                  // Content really changed - cancel the view
                  inlineViewStore.cancelView(view.id)
                }
              }
            }
          }
        }
      })
    }
    
    // Reset all-applied state when text is empty or user manually edits text (not corrections)
    if (!text || text.trim() === '') {
      allCorrectionsApplied = false
      processedErrorCount = 0
    } else if (!isCorrection && allCorrectionsApplied) {
      allCorrectionsApplied = false
      processedErrorCount = 0
    }
    if (isCorrection) {
      textEditingController?.updateText(text, true, true) // isManual=true, isCorrection=true
    }
  }
  
  function handleTextReplace(start: number, end: number, newText: string, skipYamlAdjustment = false) {
    // Check if we're in WYSIWYG mode
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      // Extract YAML frontmatter if present
      let yamlFrontmatter = ''
      let yamlOffset = 0
      if (editorText.includes('---\n')) {
        const yamlMatch = editorText.match(/^---\n[\s\S]*?\n---\n/)
        if (yamlMatch) {
          yamlFrontmatter = yamlMatch[0] // Keep the complete YAML including trailing newline
          yamlOffset = yamlMatch[0].length
        }
      }

      // Adjust positions to account for YAML offset
      // If skipYamlAdjustment is true, the positions are already TipTap positions
      // Otherwise, the positions are based on full text with YAML
      const adjustedStart = skipYamlAdjustment ? start : Math.max(0, start - yamlOffset)
      const adjustedEnd = skipYamlAdjustment ? end : Math.max(0, end - yamlOffset)

      // Use TipTap editor for text replacement with adjusted positions
      tipTapEditor.replaceText(adjustedStart, adjustedEnd, newText)

      // Update the editorText state to keep it in sync
      const updatedText = tipTapEditor.getTextContent()

      // If we have YAML, we need to prepend it back
      if (yamlFrontmatter) {
        // yamlFrontmatter already includes the trailing newline after "---\n"
        const fullText = yamlFrontmatter + updatedText
        handleTextUpdate(fullText, true)
      } else {
        handleTextUpdate(updatedText, true)
      }
      
      // InlineView position updates are handled below
    } else {
      // Original textarea-based logic
      const beforeText = editorText.substring(0, start)
      const afterText = editorText.substring(end)
      const replacedText = beforeText + newText + afterText
      
      handleTextUpdate(replacedText, true)
      
      // Only adjust scroll if not explicitly skipped (e.g., during streaming)
      const textarea = textareaElement
      if (textarea) {
        // Calculate the new cursor position after replacement
        const newCursorPosition = start + newText.length
        
        // Set cursor position to end of inserted text
        requestAnimationFrame(() => {
          textarea.setSelectionRange(newCursorPosition, newCursorPosition)
          
          // Only scroll if the cursor is not currently visible
          const textBeforeCursor = textarea.value.substring(0, newCursorPosition)
          const currentLine = textBeforeCursor.split('\n').length
          
          const computedStyle = window.getComputedStyle(textarea)
          const lineHeight = parseFloat(computedStyle.lineHeight) || 20
          const visibleHeight = textarea.clientHeight
          const visibleLines = Math.floor(visibleHeight / lineHeight)
          const scrollLine = Math.floor(textarea.scrollTop / lineHeight)
          
          // Check if cursor is outside the visible area
          const cursorAboveView = currentLine < scrollLine + 2
          const cursorBelowView = currentLine > scrollLine + visibleLines - 3
          
          if (cursorAboveView || cursorBelowView) {
            // Center the cursor line in the viewport
            const targetScrollLine = Math.max(0, currentLine - Math.floor(visibleLines / 2))
            textarea.scrollTop = targetScrollLine * lineHeight
          }
        })
      } else if (textarea) {
        // Just update cursor position without scrolling
        const newCursorPosition = start + newText.length
        requestAnimationFrame(() => {
          textarea.setSelectionRange(newCursorPosition, newCursorPosition)
        })
      }
    }
    
    // Update positions of existing inline views after text replacement
    const lengthDiff = newText.length - (end - start)
    
    // Get the current full text after replacement
    let currentFullText = editorText
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      // For WYSIWYG mode, get the updated text from TipTap
      currentFullText = tipTapEditor.getTextContent()
      // Add YAML back if present
      if (editorText.includes('---\n')) {
        const yamlMatch = editorText.match(/^---\n[\s\S]*?\n---\n/)
        if (yamlMatch) {
          currentFullText = yamlMatch[0] + currentFullText
        }
      }
    } else {
      // For textarea mode, calculate the replaced text
      const beforeText = editorText.substring(0, start)
      const afterText = editorText.substring(end)
      currentFullText = beforeText + newText + afterText
    }
    
    // Update positions of inline views that come after the replaced text
    const currentViews = get(inlineViewStore).views
    currentViews.forEach(view => {
      if (view.status !== 'applied' && view.status !== 'cancelled') {
        // If view is completely after the replacement, just shift its position
        if (view.position.start >= end) {
          // Check if the content at the new position matches the original
          const newStart = view.position.start + lengthDiff
          const newEnd = view.position.end + lengthDiff
          const currentContent = currentFullText.substring(newStart, newEnd)
          
          // Only update position if the text content remains the same
          if (currentContent === view.originalText) {
            inlineViewStore.updateView(view.id, {
              position: {
                ...view.position,
                start: newStart,
                end: newEnd
              }
            })
          } else {
            // Text changed, cancel the view
            inlineViewStore.cancelView(view.id)
          }
        }
        // If view contains the replacement area, check if it's actually affected
        else if (view.position.start < start && view.position.end > end) {
          // View spans across the replacement - the content inside changed
          inlineViewStore.cancelView(view.id)
        }
        // If view overlaps with the replacement area
        else if (!(view.position.end <= start || view.position.start >= end)) {
          // The view is somehow overlapping with the edit - cancel it
          inlineViewStore.cancelView(view.id)
        }
        // View is completely before the replacement - no change needed
        else if (view.position.end <= start) {
          // Keep the view as is
        }
      }
    })
    
    // Restore cursor position after replacement
    setTimeout(() => {
      const textarea = textareaElement
      if (textarea) {
        const newCursorPos = start + newText.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
        textarea.focus()
      }
    }, 10)
  }
  
  function handleClearErrors() {
    EditorStateService.clearErrors()
    hasChecked = false
    allCorrectionsApplied = false
    processedErrorCount = 0
  }
  
  // Handle split editor apply
  function handleSplitEditorApply(newText: string) {
    // Save final state before applying
    if (aiSegments.length > 0) {
      saveAISegmentsState('AI 변경사항 최종 적용')
    }

    const selection = get(aiFeaturesStore).selection

    if (selection) {
      // Apply to selection
      const fullText = editorText.substring(0, selection.start) +
                      newText +
                      editorText.substring(selection.end)
      handleTextUpdate(fullText, true)

      // Restore selection based on view mode
      setTimeout(() => {
        if (viewMode === 'wysiwyg' && tipTapEditor && tipTapEditor.editor) {
          // WYSIWYG mode: focus TipTap editor
          tipTapEditor.editor.commands.focus('end')
        } else {
          // Source mode: focus textarea
          const textarea = textareaElement
          if (textarea) {
            textarea.setSelectionRange(selection.start, selection.start + newText.length)
            textarea.focus()
          }
        }
      }, 100) // Increased delay to ensure setContent completes
    } else {
      // Apply to entire text, preserving YAML front matter if it exists
      const yamlMatch = editorText.match(/^---[\r\n]+[\s\S]*?[\r\n]+---[\r\n]+/)
      if (yamlMatch) {
        // Preserve YAML front matter and apply new text after it
        const yamlContent = yamlMatch[0]
        handleTextUpdate(yamlContent + newText, true)
      } else {
        // No YAML, apply text as is
        handleTextUpdate(newText, true)
      }

      // Focus editor after content update
      setTimeout(() => {
        if (viewMode === 'wysiwyg' && tipTapEditor && tipTapEditor.editor) {
          // WYSIWYG mode: focus TipTap editor at end
          tipTapEditor.editor.commands.focus('end')
        } else if (textareaElement) {
          // Source mode: focus textarea
          textareaElement.focus()
        }
      }, 100) // Increased delay to ensure setContent completes
    }

    showSplitEditor = false
    splitEditorMode = null
    
    // Reset AI segments and exit split view
    hasInitialAIState = false
    aiSegments = []
    aiFeaturesStore.exitSplitView()
    
    showStatus('변경사항이 적용되었습니다')
  }
  
  // Handle split editor cancel
  function handleSplitEditorCancel() {
    showSplitEditor = false
    splitEditorMode = null
    aiFeaturesStore.exitSplitView()
    
    // Reset initial state flag when exiting
    hasInitialAIState = false
    aiSegments = []
    
    // Close the sidebar when cancelling split view
    sidebarStore.close()
    
    showStatus('취소되었습니다')
  }
  
  // Helper function to save AI segments state for undo/redo
  function saveAISegmentsState(description: string) {
    if (aiController) {
      // Delegate to AI controller
      const segments = aiController.getAISegments()
      textEditingController?.addToHistory('ai-state', description)
    }
  }
  
  // AI segment handlers - these will be passed to ErrorDisplay
  // They need to interact with the AI controller's state
  function handleAIApply(segmentId: string) {
    // Implementation would go through AIController
    // For now, keeping the essential logic here to maintain functionality
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) return
    
    const segment = aiSegments[segmentIndex]
    if (!segment.hasChanges) return
    
    // Save global state BEFORE modification
    const wasApplied = segment.status === 'applied'
    saveAISegmentsState(`AI 세그먼트 ${segmentId} ${wasApplied ? '적용 취소 전' : '적용 전'}`)
    
    // Create new segment object for reactivity
    const updatedSegment = {
      ...segment,
      status: segment.status === 'applied' ? 'pending' : 'applied',
      selected: segment.status !== 'applied'
    }
    
    // Replace the segment in the array
    aiSegments[segmentIndex] = updatedSegment
    aiSegments = [...aiSegments]
    aiFeaturesStore.setSegments(aiSegments)
    
    // Save the new state to history
    aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
    
    // Get updated segment with history from store
    const storeState = get(aiFeaturesStore)
    const updatedSegmentWithHistory = storeState.segments.find(s => s.id === segmentId)
    if (updatedSegmentWithHistory) {
      aiSegments[segmentIndex] = updatedSegmentWithHistory
      aiSegments = [...aiSegments]
    }
    
    // Clear hover state when status changes
    const allProcessed = aiSegments.every(seg => seg.status === 'applied' || seg.status === 'skipped')
    if (allProcessed) {
      handleSegmentHover(null)
    }
  }
  
  function handleAIIgnore(segmentId: string) {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) return
    
    const segment = aiSegments[segmentIndex]
    
    // Save global state BEFORE modification
    const wasSkipped = segment.status === 'skipped'
    saveAISegmentsState(`AI 세그먼트 ${segmentId} ${wasSkipped ? '무시 취소 전' : '무시 전'}`)
    
    // Create new segment object for reactivity
    const updatedSegment = {
      ...segment,
      status: segment.status === 'skipped' ? 'pending' : 'skipped',
      selected: false
    }
    
    // Replace the segment in the array
    aiSegments[segmentIndex] = updatedSegment
    aiSegments = [...aiSegments]
    aiFeaturesStore.setSegments(aiSegments)
    
    // Save the new state to history
    aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
    
    // Get updated segment with history from store
    const storeState = get(aiFeaturesStore)
    const updatedSegmentWithHistory = storeState.segments.find(s => s.id === segmentId)
    if (updatedSegmentWithHistory) {
      aiSegments[segmentIndex] = updatedSegmentWithHistory
      aiSegments = [...aiSegments]
    }
    
    // Clear hover state when status changes
    const allProcessed = aiSegments.every(seg => seg.status === 'applied' || seg.status === 'skipped')
    if (allProcessed) {
      handleSegmentHover(null)
    }
  }
  
  // AI segment individual handlers
  async function handleAIExpand(segmentId: string) {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) {
      return
    }
    
    const segment = aiSegments[segmentIndex]
    
    // Mark this as an individual update to prevent bulk processing
    isIndividualSegmentUpdate = true
    
    // Save global state before AI processing
    saveAISegmentsState(`AI 개별 늘려쓰기 전 - ${segmentId}`)
    
    try {
      aiFeaturesStore.setSegmentProcessing(segmentId, true)
      showStatus('내용을 확장하는 중...')
      
      // Clear suggested text for streaming - only update the specific segment
      const updatedSegment = { 
        ...aiSegments[segmentIndex], 
        suggested: '',
        isProcessing: true,
        mode: 'expand' // Set mode to expand immediately
      }
      aiSegments = aiSegments.map((seg, idx) => 
        idx === segmentIndex ? updatedSegment : seg
      )
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Track accumulated text
      let accumulatedText = ''
      
      // Use suggested text if available, otherwise original
      const textToProcess = segment.suggested || segment.original
      
      const response = await AIService.expandStreaming(
        textToProcess,
        (chunk) => {
          // Accumulate chunks
          accumulatedText += chunk
          const suggested = TextSegmentService.preserveParagraphStructure(
            accumulatedText, 
            segment.paragraphBreaks
          )
          
          // Update only the specific segment with streaming text
          const streamingSegment = {
            ...aiSegments[segmentIndex],
            suggested,
            isProcessing: true,
            mode: 'expand', // Preserve the mode during streaming
            hasChanges: suggested !== segment.original // Update hasChanges during streaming
          }
          
          // Update immediately without requestAnimationFrame to avoid timing issues
          aiSegments = aiSegments.map((seg, idx) => 
            idx === segmentIndex ? streamingSegment : seg
          )
          aiFeaturesStore.setSegments(aiSegments)
        }
      )
      
      if (response.success) {
        const rawSuggested = response.result as string
        const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
        const finalReason = response.reason || (processedSuggested !== segment.original ? '세부 설명을 추가하여 내용을 보강했습니다' : '변경사항 없음')
        
        // Update the segment directly
        
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          suggested: processedSuggested,
          reason: finalReason,
          hasChanges: processedSuggested !== segment.original,
          status: processedSuggested !== segment.original ? 'pending' : 'skipped',
          mode: 'expand',
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        
        // Update the store with final state
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Save history immediately
        aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
        saveAISegmentsState('AI 개별 늘려쓰기 완료')
        
        // Reset individual update flag after a short delay
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('확장 완료')
      } else {
        // On failure, clear processing state
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Reset flag
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('확장 실패')
      }
    } catch (error) {
      
      // On error, clear processing state
      aiSegments[segmentIndex] = {
        ...aiSegments[segmentIndex],
        isProcessing: false
      }
      aiSegments = [...aiSegments]
      aiFeaturesStore.setSegments(aiSegments)
      aiFeaturesStore.setSegmentProcessing(segmentId, false)
      
      // Reset flag
      setTimeout(() => {
        isIndividualSegmentUpdate = false
      }, 100)
      
      showStatus('확장 중 오류가 발생했습니다')
    }
  }
  
  async function handleAIShrink(segmentId: string) {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) {
      return
    }
    
    const segment = aiSegments[segmentIndex]
    
    // Mark this as an individual update to prevent bulk processing
    isIndividualSegmentUpdate = true
    
    // Save global state before AI processing
    saveAISegmentsState(`AI 개별 줄여쓰기 전 - ${segmentId}`)
    
    try {
      aiFeaturesStore.setSegmentProcessing(segmentId, true)
      showStatus('내용을 축소하는 중...')
      
      // Clear suggested text for streaming - only update the specific segment
      const updatedSegment = { 
        ...aiSegments[segmentIndex], 
        suggested: '',
        isProcessing: true,
        mode: 'shrink' // Set mode to shrink immediately
      }
      aiSegments = aiSegments.map((seg, idx) => 
        idx === segmentIndex ? updatedSegment : seg
      )
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Track accumulated text
      let accumulatedText = ''
      
      // Use suggested text if available, otherwise original
      const textToProcess = segment.suggested || segment.original
      
      const response = await AIService.shrinkStreaming(
        textToProcess,
        (chunk) => {
          // Accumulate chunks
          accumulatedText += chunk
          const suggested = TextSegmentService.preserveParagraphStructure(
            accumulatedText, 
            segment.paragraphBreaks
          )
          
          // Update only the specific segment with streaming text
          const streamingSegment = {
            ...aiSegments[segmentIndex],
            suggested,
            isProcessing: true,
            mode: 'shrink', // Preserve the mode during streaming
            hasChanges: suggested !== segment.original // Update hasChanges during streaming
          }
          
          // Update immediately without requestAnimationFrame to avoid timing issues
          aiSegments = aiSegments.map((seg, idx) => 
            idx === segmentIndex ? streamingSegment : seg
          )
          aiFeaturesStore.setSegments(aiSegments)
        }
      )
      
      if (response.success) {
        const rawSuggested = response.result as string
        const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
        
        // Update the store with the new suggestion
        const finalReason = response.reason || (processedSuggested !== segment.original ? '핵심만 간결하게 요약했습니다' : '변경사항 없음')
        
        // Update the segment directly
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          suggested: processedSuggested,
          reason: finalReason,
          hasChanges: processedSuggested !== segment.original,
          status: processedSuggested !== segment.original ? 'pending' : 'skipped',
          mode: 'shrink',
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        
        // Update the store with final state
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Save history immediately
        aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
        saveAISegmentsState('AI 개별 줄여쓰기 완료')
        
        // Reset individual update flag after a short delay
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('축소 완료')
      } else {
        // On failure, clear processing state
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Reset flag
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('축소 실패')
      }
    } catch (error) {
      
      // On error, clear processing state
      aiSegments[segmentIndex] = {
        ...aiSegments[segmentIndex],
        isProcessing: false
      }
      aiSegments = [...aiSegments]
      aiFeaturesStore.setSegments(aiSegments)
      aiFeaturesStore.setSegmentProcessing(segmentId, false)
      
      // Reset flag
      setTimeout(() => {
        isIndividualSegmentUpdate = false
      }, 100)
      
      showStatus('축소 중 오류가 발생했습니다')
    }
  }
  
  async function handleAIContinue(segmentId: string, mode: 'sentence' | 'paragraph' = 'paragraph') {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) return
    
    const segment = aiSegments[segmentIndex]
    
    // Mark this as an individual update to prevent bulk processing
    isIndividualSegmentUpdate = true
    
    // Save global state before AI processing
    saveAISegmentsState(`AI 개별 이어쓰기 전 - ${segmentId}`)
    
    try {
      aiFeaturesStore.setSegmentProcessing(segmentId, true)
      showStatus('이어쓰기 중...')
      
      // Use existing suggested text if available, otherwise use original
      const baseText = segment.suggested || segment.original
      
      // Analyze text pattern for context-aware generation
      const sentences = baseText.split(/[.!?]+/).filter(s => s.trim())
      const avgSentenceLength = sentences.length > 0 
        ? Math.round(sentences.reduce((sum, s) => sum + s.trim().length, 0) / sentences.length)
        : 30
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Track accumulated continuation
      let streamedContinuation = ''
      
      const response = await AIService.continueWritingStreaming(
        baseText,
        (chunk) => {
          // Accumulate continuation chunks
          streamedContinuation += chunk
          const suggested = baseText + ' ' + streamedContinuation
          const processedSuggested = TextSegmentService.preserveParagraphStructure(suggested, segment.paragraphBreaks)
          
          // Update segment with streaming text
          aiSegments[segmentIndex] = {
            ...aiSegments[segmentIndex],
            suggested: processedSuggested,
            isProcessing: true,
            mode: 'continue'
          }
          
          // Update immediately without requestAnimationFrame to avoid timing issues
          aiSegments = [...aiSegments]
          aiFeaturesStore.setSegments(aiSegments)
        },
        {
          mode: mode,
          context: {
            position: `Segment ${segmentId}`,
            documentType: 'general',
            paragraphPattern: {
              avgSentenceLength: avgSentenceLength,
              sentenceStyle: avgSentenceLength < 30 ? 'short' : avgSentenceLength > 60 ? 'long' : 'medium'
            }
          }
        }
      )
      
      if (response.success) {
        const continuation = response.result as string
        const suggested = baseText + ' ' + continuation
        const processedSuggested = TextSegmentService.preserveParagraphStructure(suggested, segment.paragraphBreaks)
        
        // Update segment with final result
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          suggested: processedSuggested,
          reason: response.reason || (mode === 'paragraph' ? '문단을 이어서 작성했습니다' : '문장을 이어서 작성했습니다'),
          hasChanges: true,
          status: 'pending',
          mode: 'continue',
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        
        // Update the store with final state
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Save history immediately
        aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
        saveAISegmentsState('AI 개별 이어쓰기 완료')
        
        // Reset individual update flag after a short delay
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('이어쓰기 완료')
      } else {
        // On failure, clear processing state
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Reset flag
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('이어쓰기를 생성할 수 없습니다', 3500, 'error')
      }
    } catch (error) {
      
      // On error, clear processing state
      aiSegments[segmentIndex] = {
        ...aiSegments[segmentIndex],
        isProcessing: false
      }
      aiSegments = [...aiSegments]
      aiFeaturesStore.setSegments(aiSegments)
      aiFeaturesStore.setSegmentProcessing(segmentId, false)
      
      // Reset flag
      setTimeout(() => {
        isIndividualSegmentUpdate = false
      }, 100)
      
      showStatus('이어쓰기 중 오류가 발생했습니다', 3500, 'error')
    }
  }
  
  async function handleAIPrompt(segmentId: string) {
    // Do nothing - let AIActionCard handle its own inline prompt
    // This function is now deprecated in favor of inline prompts
  }
  
  // Handle segment-level prompt generation
  async function handleAISegmentPromptGenerate(segmentId: string, prompt: string, modelId: string) {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) return
    
    const segment = aiSegments[segmentIndex]
    
    // Mark this as an individual update to prevent bulk processing
    isIndividualSegmentUpdate = true
    
    // Save state before processing
    saveAISegmentsState(`AI 프롬프트 생성 전 - ${segmentId}`)
    
    // Set processing state on segment
    const processingSegment = {
      ...segment,
      isProcessing: true
    }
    aiSegments[segmentIndex] = processingSegment
    aiSegments = [...aiSegments]
    aiFeaturesStore.setSegments(aiSegments)
    
    try {
      showStatus('프롬프트로 생성 중...')
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Clear suggested to show streaming
      aiSegments[segmentIndex] = {
        ...aiSegments[segmentIndex],
        suggested: '',
        isProcessing: true
      }
      aiFeaturesStore.setSegments(aiSegments)
      
      let accumulatedText = ''
      
      // Streaming callback to update segment in real-time
      const onChunk = (chunk: string) => {
        accumulatedText += chunk
        const suggested = TextSegmentService.preserveParagraphStructure(accumulatedText, segment.paragraphBreaks)
        
        const streamingSegment = {
          ...aiSegments[segmentIndex],
          suggested,
          isProcessing: true
        }
        
        // Update directly without requestAnimationFrame for immediate feedback
        aiSegments[segmentIndex] = streamingSegment
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
      }
      
      // Use suggested text if available, otherwise original
      const textToProcess = segment.suggested || segment.original
      
      const response = await AIService.processWithPromptStreaming(
        textToProcess,
        prompt,
        onChunk,
        { modelId, mode: 'paragraph' }
      )
      
      if (response.success && response.result) {
        const processedResult = TextSegmentService.preserveParagraphStructure(
          response.result as string,
          segment.paragraphBreaks
        )
        
        // Update the segment with the prompted result
        const updatedSegment = {
          ...segment,
          suggested: processedResult,
          hasChanges: true,
          reason: `프롬프트: ${prompt}`,
          mode: 'prompt' as const,
          isProcessing: false
        }
        
        // Replace the segment in the array and trigger reactivity
        aiSegments[segmentIndex] = updatedSegment
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
        
        // Update store
        aiFeaturesStore.updateSegmentSuggestion(
          segmentId,
          processedResult,
          updatedSegment.reason,
          'prompt'
        )
        
        // Save segment history  
        aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
        
        // Get updated segment with history from store
        const storeState = get(aiFeaturesStore)
        const updatedSegmentWithHistory = storeState.segments.find(s => s.id === segmentId)
        if (updatedSegmentWithHistory) {
          aiSegments[segmentIndex] = updatedSegmentWithHistory
          aiSegments = [...aiSegments]
        }
        
        // Save global state after update
        saveAISegmentsState(`AI 프롬프트 생성 완료 - ${segmentId}`)
        
        showStatus('프롬프트 생성 완료')
      }
      
      // Reset flag after update
      isIndividualSegmentUpdate = false
    } catch (error) {
      showStatus('프롬프트 생성 실패', 3500, 'error')
      
      // Reset processing state on error
      const errorSegment = {
        ...segment,
        isProcessing: false
      }
      aiSegments[segmentIndex] = errorSegment
      aiSegments = [...aiSegments]
      aiFeaturesStore.setSegments(aiSegments)
      
      // Reset flag after update
      isIndividualSegmentUpdate = false
    }
  }
  
  async function handleAIParaphrase(segmentId: string) {
    const segmentIndex = aiSegments.findIndex(s => s.id === segmentId)
    if (segmentIndex === -1) {
      return
    }
    
    const segment = aiSegments[segmentIndex]
    
    // Mark this as an individual update to prevent bulk processing
    isIndividualSegmentUpdate = true
    
    // Save global state before AI processing
    saveAISegmentsState(`AI 개별 다시쓰기 전 - ${segmentId}`)
    
    try {
      aiFeaturesStore.setSegmentProcessing(segmentId, true)
      showStatus('다시 쓰는 중...')
      
      // Clear suggested text for streaming - only update the specific segment
      const updatedSegment = { 
        ...aiSegments[segmentIndex], 
        suggested: '',
        isProcessing: true,
        mode: 'paraphrase' // Set mode to paraphrase immediately
      }
      aiSegments = aiSegments.map((seg, idx) => 
        idx === segmentIndex ? updatedSegment : seg
      )
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Track accumulated text
      let accumulatedText = ''
      
      // Use suggested text if available, otherwise original
      const textToProcess = segment.suggested || segment.original
      
      const response = await AIService.paraphraseStreaming(
        textToProcess,
        (chunk) => {
          // Accumulate chunks
          accumulatedText += chunk
          const suggested = TextSegmentService.preserveParagraphStructure(
            accumulatedText, 
            segment.paragraphBreaks
          )
          
          // Update only the specific segment with streaming text
          const streamingSegment = {
            ...aiSegments[segmentIndex],
            suggested,
            isProcessing: true,
            mode: 'paraphrase', // Preserve the mode during streaming
            hasChanges: suggested !== segment.original // Update hasChanges during streaming
          }
          
          // Update immediately without requestAnimationFrame to avoid timing issues
          aiSegments = aiSegments.map((seg, idx) => 
            idx === segmentIndex ? streamingSegment : seg
          )
          aiFeaturesStore.setSegments(aiSegments)
        }
      )
      
      if (response.success) {
        const rawSuggested = response.result as string
        const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
        const finalReason = response.reason || (processedSuggested !== segment.original ? '더 자연스럽고 명확하게 개선했습니다' : '변경사항 없음')
        
        // Update the segment directly
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          suggested: processedSuggested,
          reason: finalReason,
          hasChanges: processedSuggested !== segment.original,
          status: processedSuggested !== segment.original ? 'pending' : 'skipped',
          mode: 'paraphrase',
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        
        // Update the store with final state
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Save history immediately
        aiFeaturesStore.saveSegmentHistory(segmentId, 'ai-action')
        saveAISegmentsState('AI 개별 다시쓰기 완료')
        
        // Reset individual update flag after a short delay
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('다시쓰기 완료')
      } else {
        // On failure, clear processing state
        aiSegments[segmentIndex] = {
          ...aiSegments[segmentIndex],
          isProcessing: false
        }
        aiSegments = [...aiSegments]
        aiFeaturesStore.setSegments(aiSegments)
        aiFeaturesStore.setSegmentProcessing(segmentId, false)
        
        // Reset flag
        setTimeout(() => {
          isIndividualSegmentUpdate = false
        }, 100)
        
        showStatus('다시쓰기 실패')
      }
    } catch (error) {
      
      // On error, clear processing state
      aiSegments[segmentIndex] = {
        ...aiSegments[segmentIndex],
        isProcessing: false
      }
      aiSegments = [...aiSegments]
      aiFeaturesStore.setSegments(aiSegments)
      aiFeaturesStore.setSegmentProcessing(segmentId, false)
      
      // Reset flag
      setTimeout(() => {
        isIndividualSegmentUpdate = false
      }, 100)
      
      showStatus('다시쓰기 중 오류가 발생했습니다')
    }
  }
  
  function handleAIApplyAll() {
    // Toggle functionality only - never return to editor
    const hasAppliedSegments = aiSegments.some(s => s.status === 'applied')
    
    if (hasAppliedSegments) {
      // If some segments are applied, unapply all
      aiSegments = aiSegments.map(s => {
        if (s.status === 'applied') {
          return {
            ...s,
            status: 'pending',
            selected: false
          }
        }
        return s
      })
    } else {
      // If no segments are applied, apply all segments with changes
      // Include both pending and skipped segments
      aiSegments = aiSegments.map(s => {
        if (s.hasChanges && (s.status === 'pending' || s.status === 'skipped')) {
          return {
            ...s,
            selected: true,
            status: 'applied'
          }
        }
        return s
      })
    }
    
    aiFeaturesStore.setSegments(aiSegments)
  }
  
  function handleAIRejectAll() {
    // Cancel and go back to editor
    handleSplitEditorCancel()
  }
  
  function handleAIApplyAndExit() {
    // Apply all changes and go back to editor
    if (!aiSegments || aiSegments.length === 0) {
      handleSplitEditorCancel()
      return
    }
    
    // Combine segments - use suggested for applied segments, original for others
    const combinedText = aiSegments.map(segment => {
      if (segment.status === 'applied' && segment.suggested) {
        return segment.suggested
      }
      return segment.original || ''
    }).filter(text => text.length > 0).join('\n\n')
    
    if (combinedText.trim()) {
      // Apply the combined text to the editor
      handleSplitEditorApply(combinedText)
      
      // Close the sidebar after applying changes
      setTimeout(() => {
        sidebarStore.close()
      }, 100)
    } else {
      handleSplitEditorCancel()
    }
  }
  
  function handleAIContinueEditing() {
    // Just hide the completion message, keep all statuses intact
    // The segments keep their applied/skipped status
    // This allows users to continue editing or change their decisions
    aiSegments = [...aiSegments]
    aiFeaturesStore.setSegments(aiSegments)
  }
  
  // Bulk AI operations
  async function handleAIParaphraseAll() {
    if (!aiSegments.length) return
    
    // Save state before processing all segments
    saveAISegmentsState('AI 전체 다시쓰기')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    try {
      aiFeaturesStore.setProcessing(true)
      showStatus('모든 문단을 다시 쓰는 중...')
      
      // Save original segments before clearing for skeleton
      const originalSegments = [...aiSegments]
      
      // Set all segments to processing state and clear suggested to show skeleton
      aiSegments = aiSegments.map(segment => ({
        ...segment,
        isProcessing: true,
        suggested: '',  // Clear suggested to show skeleton
        hasChanges: false,
        status: 'pending'
      }))
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Use original segments for processing (with actual data)
      const promises = originalSegments.map(async (segment, index) => {
        const response = await AIService.paraphrase(segment.original)
        if (response.success) {
          const rawSuggested = response.result as string
          const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
          
          // Return result with index for mapping
          return {
            index,
            suggested: processedSuggested,
            hasChanges: processedSuggested !== segment.original,
            status: processedSuggested !== segment.original ? 'pending' : 'skipped',
            selected: false,
            mode: 'paraphrase',
            reason: response.reason || (processedSuggested !== segment.original ? '더 자연스럽고 명확하게 개선했습니다' : '변경사항 없음'),
            isProcessing: false
          }
        }
        return { index, isProcessing: false, suggested: segment.suggested || '' }
      })
      
      const results = await Promise.all(promises)
      
      // Update aiSegments by index to preserve other properties
      results.forEach(result => {
        const { index, ...updates } = result
        aiSegments[index] = {
          ...aiSegments[index],
          ...updates
        }
      })
      
      aiFeaturesStore.setSegments(aiSegments)
      
      // Save history for each updated segment
      updatedSegments.forEach(segment => {
        if (segment.hasChanges) {
          aiFeaturesStore.saveSegmentHistory(segment.id, 'bulk-action')
        }
      })
      
      showStatus('다시쓰기 완료')
    } catch (error) {
      showStatus('다시쓰기 실패')
    } finally {
      aiFeaturesStore.setProcessing(false)
      isBulkProcessing = false  // Reset bulk processing flag
    }
  }
  
  async function handleAIPromptAll(prompt?: string, modelId?: string) {
    // Only process if both prompt and modelId are provided (from inline prompt)
    if (!prompt || !modelId) {
      // Do nothing - let the sidebar handle its own inline prompt UI
      return
    }
    
    // If no AI segments (error card mode), process the full text
    if (!aiSegments.length) {
      try {
        const { AIService } = await import('$lib/services/AIService')
        const response = await AIService.processWithPrompt(
          editorText,
          prompt,
          { modelId, mode: 'paragraph' }
        )
        
        if (response.success && response.result) {
          editorText = response.result
          showStatus('프롬프트 처리 완료')
        }
      } catch (error) {
        showStatus('프롬프트 처리 중 오류가 발생했습니다')
      }
      return
    }
    
    // Save state before processing all segments
    saveAISegmentsState('AI 전체 프롬프트')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    try {
      const { AIService } = await import('$lib/services/AIService')
      
      // Save original segments before clearing for skeleton
      const originalSegments = [...aiSegments]
      
      // Mark all segments as processing and clear suggested to show skeleton
      aiSegments = aiSegments.map(segment => ({ 
        ...segment, 
        isProcessing: true,
        suggested: '',  // Clear suggested to show skeleton
        hasChanges: false,
        status: 'pending'
      }))
      aiFeaturesStore.setSegments(aiSegments)
      
      // Process all segments in parallel (use original segments with actual data)
      const promises = originalSegments.map(async (segment, index) => {
        try {
          const response = await AIService.processWithPrompt(
            segment.original,
            prompt,
            { modelId, mode: 'paragraph' }
          )
          
          if (response.success && response.result) {
            // Create history entry
            const historyEntry = {
              suggested: response.result,
              reason: `프롬프트: "${prompt}"`,
              status: 'pending' as const,
              mode: 'prompt' as const
            }
            
            // Return result with index for mapping
            return {
              index,
              suggested: response.result,
              hasChanges: true,
              isProcessing: false,
              mode: 'prompt' as const,
              reason: `프롬프트: "${prompt}"`,
              customPrompt: prompt,
              status: 'pending' as const,
              history: [...segment.history, historyEntry],
              historyIndex: segment.history.length
            }
          } else {
            return { index, isProcessing: false, suggested: segment.suggested || '' }
          }
        } catch (error) {
          return { index, isProcessing: false, suggested: segment.suggested || '' }
        }
      })
      
      // Wait for all to complete and update
      const results = await Promise.all(promises)
      
      // Update aiSegments by index to preserve other properties
      results.forEach(result => {
        const { index, ...updates } = result
        aiSegments[index] = {
          ...aiSegments[index],
          ...updates
        }
      })
      
      aiFeaturesStore.setSegments(aiSegments)
      
      showStatus(`프롬프트로 ${aiSegments.filter(s => s.hasChanges).length}개 문단 처리 완료`)
    } catch (error) {
      showStatus('프롬프트 처리 중 오류가 발생했습니다')
      
      // Remove processing state from all segments on error
      aiSegments = aiSegments.map(s => ({ ...s, isProcessing: false }))
      aiFeaturesStore.setSegments(aiSegments)
    } finally {
      isBulkProcessing = false  // Reset bulk processing flag
    }
  }
  
  // Handle bulk prompt from SplitEditor
  async function handleSplitBulkPrompt(prompt: string, modelId: string) {
    if (!prompt || !modelId) return
    
    const { AIService } = await import('$lib/services/AIService')
    
    // Save state before processing
    saveAISegmentsState('Bulk 프롬프트 처리')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    // Mark all segments as processing
    aiSegments = aiSegments.map(segment => ({ ...segment, isProcessing: true }))
    aiFeaturesStore.setSegments(aiSegments)
    
    // Process all segments in parallel
    const promises = aiSegments.map(async (segment) => {
      try {
        const response = await AIService.processWithPrompt(
          segment.original,
          prompt,
          { modelId, mode: 'paragraph' }
        )
        
        if (response.success && response.result) {
          // Create history entry
          const historyEntry = {
            suggested: response.result,
            reason: `프롬프트: "${prompt}"`,
            status: 'pending' as const,
            mode: 'prompt' as const
          }
          
          // Add to segment with updated history
          const updatedSegment = {
            ...segment,
            suggested: response.result,
            hasChanges: true,
            isProcessing: false,
            mode: 'prompt' as const,
            reason: `프롬프트: "${prompt}"`,
            customPrompt: prompt,
            status: 'pending' as const,  // Mark as pending to show changes
            history: [...segment.history, historyEntry],
            historyIndex: segment.history.length  // Point to the new entry
          }
          
          return updatedSegment
        } else {
          return { ...segment, isProcessing: false }
        }
      } catch (error) {
        // console.error(`Failed to process segment ${segment.id}:`, error)
        return { ...segment, isProcessing: false }
      }
    })
    
    // Wait for all to complete and update
    const processedSegments = await Promise.all(promises)
    
    // Update segments with proper state
    aiSegments = [...processedSegments]
    aiFeaturesStore.setSegments(processedSegments)
    
    // Show success message
    showStatus(`프롬프트로 ${processedSegments.filter(s => s.hasChanges).length}개 세그먼트 처리 완료`)
    
    // Reset bulk processing flag
    isBulkProcessing = false
  }
  
  async function handleAIExpandAll() {
    if (!aiSegments.length) return
    
    // Save state before processing all segments
    saveAISegmentsState('AI 전체 늘려쓰기')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    try {
      aiFeaturesStore.setProcessing(true)
      showStatus('모든 문단을 확장하는 중...')
      
      // Save original segments before clearing for skeleton
      const originalSegments = [...aiSegments]
      
      // Set all segments to processing state and clear suggested to show skeleton
      aiSegments = aiSegments.map(segment => ({
        ...segment,
        isProcessing: true,
        suggested: '',  // Clear suggested to show skeleton
        hasChanges: false,
        status: 'pending'
      }))
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Use original segments for processing (with actual data)
      const promises = originalSegments.map(async (segment, index) => {
        const response = await AIService.expand(segment.original)
        if (response.success) {
          const rawSuggested = response.result as string
          const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
          
          // Return result with index for mapping
          return {
            index,
            suggested: processedSuggested,
            hasChanges: processedSuggested !== segment.original,
            status: processedSuggested !== segment.original ? 'pending' : 'skipped',
            selected: false,
            mode: 'expand',
            reason: response.reason || (processedSuggested !== segment.original ? '세부 설명을 추가하여 내용을 보강했습니다' : '변경사항 없음'),
            isProcessing: false
          }
        }
        return { index, isProcessing: false, suggested: segment.suggested || '' }
      })
      
      const results = await Promise.all(promises)
      
      // Update aiSegments by index to preserve other properties
      results.forEach(result => {
        const { index, ...updates } = result
        aiSegments[index] = {
          ...aiSegments[index],
          ...updates
        }
      })
      
      aiFeaturesStore.setSegments(aiSegments)
      
      // Save history for each updated segment
      aiSegments.forEach(segment => {
        if (segment.hasChanges) {
          aiFeaturesStore.saveSegmentHistory(segment.id, 'bulk-action')
        }
      })
      
      showStatus('확장 완료')
    } catch (error) {
      showStatus('확장 실패')
    } finally {
      aiFeaturesStore.setProcessing(false)
      isBulkProcessing = false  // Reset bulk processing flag
    }
  }
  
  async function handleAIContinueAll() {
    if (!aiSegments.length) return
    
    // Save state before processing all segments
    saveAISegmentsState('AI 전체 이어쓰기')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    try {
      aiFeaturesStore.setProcessing(true)
      showStatus('모든 문단을 이어쓰기 중...')
      
      // Save original segments before clearing for skeleton
      const originalSegments = [...aiSegments]
      
      // Set all segments to processing state and clear suggested to show skeleton
      aiSegments = aiSegments.map(segment => ({
        ...segment,
        isProcessing: true,
        suggested: '',  // Clear suggested to show skeleton
        hasChanges: false,
        status: 'pending'
      }))
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Use original segments for processing (with actual data)
      const promises = originalSegments.map(async (segment, index) => {
        // Use existing suggested text if available, otherwise use original
        const baseText = segment.suggested || segment.original
        
        // Analyze text pattern for context-aware generation
        const sentences = baseText.split(/[.!?]+/).filter(s => s.trim())
        const avgSentenceLength = sentences.length > 0 
          ? Math.round(sentences.reduce((sum, s) => sum + s.trim().length, 0) / sentences.length)
          : 30
        
        const response = await AIService.continueWriting(baseText, {
          mode: 'paragraph',
          context: {
            position: `Segment ${segment.id}`,
            documentType: 'general',
            paragraphPattern: {
              avgSentenceLength: avgSentenceLength,
              sentenceStyle: avgSentenceLength < 30 ? 'short' : avgSentenceLength > 60 ? 'long' : 'medium',
              targetLength: Math.min(200, Math.max(100, baseText.length * 0.5)),
              targetSentences: Math.min(5, Math.max(3, sentences.length))
            }
          }
        })
        if (response.success) {
          const continuation = response.result as string
          const suggested = baseText + ' ' + continuation
          const processedSuggested = TextSegmentService.preserveParagraphStructure(suggested, segment.paragraphBreaks)
          
          return {
            index,
            suggested: processedSuggested,
            hasChanges: true,
            reason: response.reason || '문단을 이어서 작성했습니다',
            mode: 'continue' as const,
            status: 'pending' as const,
            isProcessing: false
          }
        }
        return { index, isProcessing: false, suggested: segment.suggested || '' }
      })
      
      const results = await Promise.all(promises)
      
      // Update aiSegments by index to preserve other properties
      results.forEach(result => {
        const { index, ...updates } = result
        aiSegments[index] = {
          ...aiSegments[index],
          ...updates
        }
      })
      
      aiFeaturesStore.setSegments(aiSegments)
      
      // Save history for each updated segment
      aiSegments.forEach(segment => {
        if (segment.hasChanges) {
          aiFeaturesStore.saveSegmentHistory(segment.id, 'bulk-action')
        }
      })
      
      // Save global state after all updates
      saveAISegmentsState('AI 전체 이어쓰기 완료')
      
      showStatus('모든 문단 이어쓰기 완료')
    } catch (error) {
      showStatus('이어쓰기 중 오류가 발생했습니다', 3500, 'error')
    } finally {
      aiFeaturesStore.setProcessing(false)
      aiSegments.forEach(segment => {
        aiFeaturesStore.setSegmentProcessing(segment.id, false)
      })
      isBulkProcessing = false  // Reset bulk processing flag
    }
  }
  
  async function handleAIShrinkAll() {
    if (!aiSegments.length) return
    
    // Save state before processing all segments
    saveAISegmentsState('AI 전체 줄여쓰기')
    
    // Set bulk processing flag to prevent SegmentProcessorController interference
    isBulkProcessing = true
    
    try {
      aiFeaturesStore.setProcessing(true)
      showStatus('모든 문단을 축소하는 중...')
      
      // Save original segments before clearing for skeleton
      const originalSegments = [...aiSegments]
      
      // Set all segments to processing state and clear suggested to show skeleton
      aiSegments = aiSegments.map(segment => ({
        ...segment,
        isProcessing: true,
        suggested: '',  // Clear suggested to show skeleton
        hasChanges: false,
        status: 'pending'
      }))
      aiFeaturesStore.setSegments(aiSegments)
      
      const { AIService } = await import('$lib/services/AIService')
      const { TextSegmentService } = await import('$lib/services/TextSegmentService')
      
      // Use original segments for processing (with actual data)
      const promises = originalSegments.map(async (segment, index) => {
        const response = await AIService.shrink(segment.original)
        if (response.success) {
          const rawSuggested = response.result as string
          const processedSuggested = TextSegmentService.preserveParagraphStructure(rawSuggested, segment.paragraphBreaks)
          
          // Return result with index for mapping
          return {
            index,
            suggested: processedSuggested,
            hasChanges: processedSuggested !== segment.original,
            status: processedSuggested !== segment.original ? 'pending' : 'skipped',
            selected: false,
            mode: 'shrink',
            reason: response.reason || (processedSuggested !== segment.original ? '핵심만 간결하게 요약했습니다' : '변경사항 없음'),
            isProcessing: false
          }
        }
        return { index, isProcessing: false, suggested: segment.suggested || '' }
      })
      
      const results = await Promise.all(promises)
      
      // Update aiSegments by index to preserve other properties
      results.forEach(result => {
        const { index, ...updates } = result
        aiSegments[index] = {
          ...aiSegments[index],
          ...updates
        }
      })
      
      aiFeaturesStore.setSegments(aiSegments)
      
      // Save history for each updated segment
      aiSegments.forEach(segment => {
        if (segment.hasChanges) {
          aiFeaturesStore.saveSegmentHistory(segment.id, 'bulk-action')
        }
      })
      
      showStatus('축소 완료')
    } catch (error) {
      showStatus('축소 실패')
    } finally {
      aiFeaturesStore.setProcessing(false)
      isBulkProcessing = false  // Reset bulk processing flag
    }
  }
  
  function handleSegmentHover(segmentId: string | null) {
    hoveredSegmentId = segmentId
  }
  
  // Segment undo/redo handlers
  function handleSegmentUndo(segmentId: string) {
    // Save current state before undo
    saveAISegmentsState(`세그먼트 ${segmentId} 실행 취소`)
    
    // Perform undo
    aiFeaturesStore.undoSegment(segmentId)
    
    // Refresh local state
    const updatedState = get(aiFeaturesStore)
    aiSegments = [...updatedState.segments]
  }
  
  function handleSegmentRedo(segmentId: string) {
    // Save current state before redo
    saveAISegmentsState(`세그먼트 ${segmentId} 다시 실행`)
    
    // Perform redo
    aiFeaturesStore.redoSegment(segmentId)
    
    // Refresh local state
    const updatedState = get(aiFeaturesStore)
    aiSegments = [...updatedState.segments]
  }
  
  function canSegmentUndo(segmentId: string): boolean {
    return aiFeaturesStore.canUndoSegment(segmentId)
  }
  
  function canSegmentRedo(segmentId: string): boolean {
    return aiFeaturesStore.canRedoSegment(segmentId)
  }
  
  // Helper function to check if sidebar should auto-close
  function checkAndCloseSidebarIfEmpty() {
    setTimeout(() => {
      const updatedState = get(inlineViewStore)
      const activeViews = updatedState.views.filter(
        v => v.status !== 'applied' && v.status !== 'cancelled'
      )
      if (activeViews.length === 0 && errorState.errors.length === 0 && !showSplitEditor) {
        // No more content, close sidebar
        sidebarStore.close(true) // true = auto-close
      }
    }, 150)
  }
  
  // Inline view card handlers
  function handleInlineViewApply(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view && view.suggestedText) {
      inlineViewController?.handleApplyView(viewId, view.suggestedText)
      
      // Check if this was the last active view
      checkAndCloseSidebarIfEmpty()
    }
  }
  
  function handleInlineViewIgnore(viewId: string) {
    inlineViewController?.handleCancelView(viewId)
    
    // Check if this was the last active view
    checkAndCloseSidebarIfEmpty()
  }
  
  async function handleInlineViewParaphrase(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view) {
      // Use suggested text if available, otherwise original
      const textToProcess = view.suggestedText || view.originalText
      await inlineViewController?.processSelection(
        textToProcess,
        view.position.start,
        view.position.end,
        'paraphrase'
      )
    }
  }
  
  async function handleInlineViewExpand(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view) {
      // Use suggested text if available, otherwise original
      const textToProcess = view.suggestedText || view.originalText
      await inlineViewController?.processSelection(
        textToProcess,
        view.position.start,
        view.position.end,
        'expand'
      )
    }
  }
  
  async function handleInlineViewShrink(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view) {
      // Use suggested text if available, otherwise original
      const textToProcess = view.suggestedText || view.originalText
      await inlineViewController?.processSelection(
        textToProcess,
        view.position.start,
        view.position.end,
        'shrink'
      )
    }
  }
  
  async function handleInlineViewContinue(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view) {
      // Use suggested text if available, otherwise original
      const textToProcess = view.suggestedText || view.originalText
      await inlineViewController?.processSelection(
        textToProcess,
        view.position.start,
        view.position.end,
        'continue'
      )
    }
  }
  
  async function handleInlineViewPrompt(viewId: string) {
    // Get the latest view from store directly
    const storeState = get(inlineViewStore)
    const view = storeState.views.find(v => v.id === viewId)
    if (view) {
      // Show prompt dialog for this specific inline view
      showGenerationPrompt = true
      selectedTextForGeneration = view.suggestedText || view.originalText
      generationSelectionRange = { 
        start: view.position.start,
        end: view.position.end 
      }
    }
  }
  
  async function handleInlineViewGeneratePrompt(viewId: string, prompt: string, modelId: string) {
    const view = inlineViews.find(v => v.id === viewId)
    if (!view) return
    
    // Set processing state on the segment
    if (view.segments && view.segments.length > 0) {
      const updatedSegments = [{
        ...view.segments[0],
        isProcessing: true
      }]
      inlineViewStore.updateView(viewId, {
        status: 'processing',
        segments: updatedSegments
      })
    } else {
      inlineViewStore.updateView(viewId, {
        status: 'processing'
      })
    }
    
    try {
      // Import AIService dynamically
      const { AIService } = await import('$lib/services/AIService')
      
      // Process with prompt for this inline view
      const response = await AIService.processWithPrompt(
        view.suggestedText || view.originalText,
        prompt,
        {
          modelId,
          mode: 'paragraph'
        }
      )
      
      if (response.success && response.result) {
        // Update segments if this is a card view
        const segments = view.segments && view.segments.length > 0 ? [{
          ...view.segments[0],
          suggested: response.result,
          hasChanges: response.result !== view.originalText,
          mode: 'prompt' as const,
          customPrompt: prompt,
          reason: prompt,
          isProcessing: false
        }] : undefined
        
        // Update the inline view with the generated text through store
        inlineViewStore.addToHistory(viewId, response.result, prompt, view.originalText, 'prompt')
        inlineViewStore.updateView(viewId, {
          suggestedText: response.result,
          status: 'completed',
          mode: 'prompt',
          segments
        })
        
        showStatus('프롬프트 생성 완료')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      showStatus(`프롬프트 생성 실패: ${errorMessage}. 재시도 버튼을 클릭하세요.`, 5000)
      
      // Create retry callback
      const retryCallback = async () => {
        // Clear error and retry the same request
        await handleInlineViewGeneratePrompt(viewId, prompt, modelId)
      }
      
      // Set error state on segment with retry callback
      if (view.segments && view.segments.length > 0) {
        const updatedSegments = [{
          ...view.segments[0],
          isProcessing: false,
          error: errorMessage,
          retryCallback
        }]
        inlineViewStore.updateView(viewId, {
          status: 'completed',
          segments: updatedSegments
        })
      } else {
        // Create error segment if no segments exist
        const errorSegment: Segment = {
          id: viewId,
          original: view.originalText,
          suggested: '',
          hasChanges: false,
          mode: 'prompt' as const,
          customPrompt: prompt,
          reason: '',
          status: 'pending',
          selected: false,
          position: view.position,
          history: [],
          historyIndex: -1,
          error: errorMessage,
          retryCallback
        }
        
        inlineViewStore.updateView(viewId, {
          status: 'completed',
          segments: [errorSegment]
        })
      }
    }
  }
  
  // Inline view undo/redo handlers
  function handleInlineViewUndo(viewId: string) {
    inlineViewStore.undoView(viewId)
  }
  
  function handleInlineViewRedo(viewId: string) {
    inlineViewStore.redoView(viewId)
  }
  
  function canInlineViewUndo(viewId: string): boolean {
    return inlineViewStore.canUndo(viewId)
  }
  
  function canInlineViewRedo(viewId: string): boolean {
    return inlineViewStore.canRedo(viewId)
  }
  
  // Storage listener for cross-tab communication
  useStorageListener('korean-grammar:usage', (newValue, oldValue) => {
    // Usage data changed in another tab, no action needed here
  })
  
  useStorageListener('grammar-check-usage', (newValue, oldValue) => {
    // Usage data changed in another tab, no action needed here
  })
  
  // State to track pending AI analysis after grammar check
  let pendingAIAnalysis = $state(false)
  
  // State to track grammar checking status
  let isGrammarChecking = $state(false)
  
  // Watch for grammar check completion
  // This effect is now redundant since we handle it in onCheckComplete callback
  // Keeping it disabled but not removed for reference
  /*
  $effect(() => {
    // When grammar check completes and AI analysis is pending
    if (pendingAIAnalysis && !isGrammarChecking && hasChecked) {
      pendingAIAnalysis = false
      // Wait a bit to ensure errors are loaded
      setTimeout(() => {
        handleAIAnalysis()
      }, 300)
    }
  })
  */

  // Listen for reset event from upgrade modal
  useCustomEvent('reset-grammar-check-state', () => {
    // Reset the grammar check controller state when upgrade modal closes
    if (grammarCheckController) {
      grammarCheckController.resetCheckingState()
    }
    // Also force reset the local isGrammarChecking state
    isGrammarChecking = false

    // Reset AI split view and segments
    showSplitEditor = false
    splitEditorMode = null
    aiSegments = []

    // Reset inline views - this was causing the button to remain disabled
    inlineViews = []

    // Reset AI controller state
    if (aiController) {
      aiController.resetAIState()
    }

    // Reset AI features store to ensure processing state is cleared
    aiFeaturesStore.reset()
    aiFeaturesStore.setProcessing(false)
  })

  // Custom event listeners for shortcuts
  useCustomEvent('shortcut:unified-check', () => {
    // Check settings to determine what to run
    const settings = settingsStore.get()
    const grammarEnabled = settings.unifiedCheck?.grammarEnabled ?? true
    const aiEnabled = settings.unifiedCheck?.aiEnabled ?? false

    // Run both checks sequentially if both enabled
    if (grammarEnabled && aiEnabled) {
      pendingAIAnalysis = true  // Mark AI analysis as pending
      handleCheckGrammar()
      // AI analysis will automatically run when grammar check completes
    } else if (grammarEnabled) {
      handleCheckGrammar()
    } else if (aiEnabled) {
      handleAIAnalysis()
    }
  })

  // Listen for mode restoration event from ErrorDisplay
  useCustomEvent('restore-editor-mode', () => {
    // Always switch to reading mode (wysiwyg) after apply/reject all
    if (viewMode !== 'wysiwyg') {
      viewMode = 'wysiwyg'
      editorViewStore.setMode('wysiwyg')
      originalViewModeForGrammarCheck = null
    }

    // Close sidebar when switching to reading mode
    sidebarStore.close()
  })
  useCustomEvent('shortcut:generate', () => handleShowGenerationPrompt())
  useCustomEvent('shortcut:clear-text', () => handleClearText())
  useCustomEvent('shortcut:toggle-theme', () => toggleTheme())
  useCustomEvent('shortcut:apply-all', () => handleApplyAllCorrections())
  
  // AI feature shortcuts
  useCustomEvent('shortcut:paraphrase', () => handleParaphrase())
  useCustomEvent('shortcut:expand', () => handleExpand())
  useCustomEvent('shortcut:shrink', () => handleShrink())
  useCustomEvent('shortcut:summarize', () => handleSummarize())
  useCustomEvent('shortcut:continue-sentence', () => handleContinue('sentence'))
  useCustomEvent('shortcut:continue-paragraph', () => handleContinue('paragraph'))
  useCustomEvent('shortcut:critic', () => handleCritic())
  useCustomEvent('shortcut:fullscreen', () => toggleFullscreen())

  // Restore view mode when upgrade prompt closes due to text length limit
  useCustomEvent('restore-view-mode-after-limit', () => {
    restoreOriginalViewMode()
  })
  
  // useCustomEvent('shortcut:focus-editor', () => {
  //   const textarea = textareaElement
  //   if (textarea) {
  //     textarea.focus()
  //     // Select all text if empty, otherwise restore last cursor position
  //     if (editorText.trim() === '') {
  //       textarea.select()
  //     } else {
  //       // Restore last known cursor position
  //       requestAnimationFrame(() => {
  //         textarea.setSelectionRange(lastCursorPosition.start, lastCursorPosition.end)
  //       })
  //     }
  //   }
  // })
  
  useCustomEvent('shortcut:focus-explorer', () => {
    // Check if already in FileSystemWrapper mode
    const fileExplorerEl = document.querySelector('.file-explorer') as HTMLElement
    if (!fileExplorerEl) {
      // Not in FileSystemWrapper mode, toggle to it
      window.dispatchEvent(new CustomEvent('shortcut:toggle-file-explorer'))
    } else {
      // Already in FileSystemWrapper mode, element will handle focus
    }
  })
  
  useCustomEvent('shortcut:unfocus-editor', () => {
    const textarea = textareaElement
    if (textarea && document.activeElement === textarea) {
      // Store cursor position before losing focus
      lastCursorPosition = {
        start: textarea.selectionStart,
        end: textarea.selectionEnd
      }
      textarea.blur()
    }
  })

  // Listen for blur events from the editor to store cursor position
  useCustomEvent('editor:blur', (event) => {
    if (event.detail) {
      lastCursorPosition = {
        start: event.detail.start,
        end: event.detail.end
      }
    }
  })
  
  // Listen for apply-correction events from editor (Enter key on error)
  useCustomEvent('apply-correction', (event) => {
    if (event.detail && event.detail.errorId && event.detail.correctionId) {
      handleApplyCorrection(event.detail.errorId, event.detail.correctionId)
    }
  })
  
  // Keyboard handler for paste shortcut
  useKeyboard('cmd+v', (e) => {
    // Don't focus textarea if GenerationBar input is active
    const activeElement = document.activeElement
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Let the GenerationBar handle its own paste
      return
    }
    
    // Check if title input is focused
    if (activeElement && activeElement.classList.contains('title-input')) {
      // Let the title input handle its own paste
      return
    }
    
    // Focus the main editor textarea to ensure paste works
    if (textareaElement && document.activeElement !== textareaElement) {
      textareaElement.focus()
      // Let the browser handle the actual paste
    }
  }, { capture: true })
  
  // Save state when component is destroyed
  onDestroy(() => {
    // Clear any pending save timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout)
      saveTimeout = null
    }
    
    // Save current tab's state before unmounting
    if (currentTab?.id && !isRestoringState) {
      fileStore.saveTabCheckState(currentTab.id, errorState, inlineViews)
    }
  })
  
  // Handle keyboard shortcuts for undo/redo and search
  function handleKeyDown(e: KeyboardEvent) {
    if (!currentTab?.id) return

    // Ignore key events during IME composition
    if (e.isComposing || (e as any).keyCode === 229) {
      return
    }

    // PRIORITY: Handle ESC to close search (before any other checks)
    // This needs to work regardless of focus state
    if (e.key === 'Escape') {
      const currentSearchState = get(searchStore)
      if (currentSearchState.isVisible) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        handleSearchClose()
        return
      }
    }

    // Check if currently focused element is an input/textarea
    const activeElement = document.activeElement as HTMLElement
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.classList?.contains('search-input') ||
      activeElement.classList?.contains('title-input') ||
      activeElement.contentEditable === 'true'
    )

    // Don't handle shortcuts when any input is focused except for search shortcut
    if (isInputFocused) {
      // Only handle search shortcut (Cmd+F) when not in search input
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isSearchKey = isMac ? (e.metaKey && e.key === 'f') : (e.ctrlKey && e.key === 'f')

      if (isSearchKey && !activeElement.classList?.contains('search-input')) {
        e.preventDefault()
        searchStore.show()
      }
      return
    }

    // Handle search shortcut (Cmd+F or Ctrl+F)
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const isSearchKey = isMac ? (e.metaKey && e.key === 'f') : (e.ctrlKey && e.key === 'f')

    if (isSearchKey) {
      e.preventDefault()
      searchStore.show()  // Always show, don't toggle
      return
    }
    
    const isUndo = isMac ? (e.metaKey && e.key === 'z' && !e.shiftKey) : (e.ctrlKey && e.key === 'z' && !e.shiftKey)
    const isRedo = isMac ? (e.metaKey && e.shiftKey && e.key === 'z') : ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z'))
    
    if (isUndo) {
      e.preventDefault()
      const previousContent = fileStore.undo(currentTab.id)
      if (previousContent !== null) {
        const currentTextState = get(textStore)
        textStore.set({
          original: currentTextState.original,
          current: previousContent,
          corrected: '',
          preview: ''
        })
      }
    } else if (isRedo) {
      e.preventDefault()
      const nextContent = fileStore.redo(currentTab.id)
      if (nextContent !== null) {
        const currentTextState = get(textStore)
        textStore.set({
          original: currentTextState.original,
          current: nextContent,
          corrected: '',
          preview: ''
        })
      }
    }
  }
  
  // Attach keyboard event listener to document
  // Use capture phase to ensure ESC for search is handled before other handlers
  $effect(() => {
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  })
  
  // Initialize file system on mount
  onMount(async () => {
    
    try {
      // Load documents on initialization
      await fileStore.loadDocuments()
      
      // Don't auto-open any documents - let user choose what to open
      // Only create a new document if vault is completely empty (no existing documents)

      // Check if we're in demo mode - if so, skip auto-creation as demo content will be set up
      const urlParams = new URLSearchParams(window.location.search)
      const isDemoMode = urlParams.get('demo') === 'true'

      const state = get(fileStore)
      if (state.documents.length === 0 && !isDemoMode) {
        // Vault is empty, create a default document for user convenience (but not in demo mode)
        const docId = await fileStore.createNewDocument()
        await fileStore.openFile(docId)
        postingPromptStore.loadForTab(docId)

        // 첫 문서 생성 시 포스팅 프롬프트 자동 열기
        setTimeout(() => {
          unifiedPromptStore.show('posting')
          window.dispatchEvent(new CustomEvent('status-message', {
            detail: {
              message: '첫 문서가 생성되었습니다. 포스팅 프롬프트를 설정해주세요.',
              type: 'info'
            }
          }))
        }, 500)
      }
      // Don't auto-open existing documents - user should explicitly choose
    } catch (error) {
      // console.error('Failed to initialize file system:', error)
      // Create a fallback document if initialization fails
      const docId = await fileStore.createNewDocument('untitled')
      await fileStore.openFile(docId)
      postingPromptStore.loadForTab(docId)
      
      // 첫 문서 생성 시 포스팅 프롬프트 자동 열기
      setTimeout(() => {
        unifiedPromptStore.show('posting')
        window.dispatchEvent(new CustomEvent('status-message', { 
          detail: { 
            message: '첫 문서가 생성되었습니다. 포스팅 프롬프트를 설정해주세요.', 
            type: 'info' 
          } 
        }))
      }, 500)
    }
    
    return () => {
      grammarCheckController?.destroy()
    }
  })
  
  // Track last reload time to detect external changes
  let lastReloadTime = $state<number>(0)
  
  // Sync active tab content with text store

  $effect(() => {
    // Check if we switched to a different tab (use separate tracker for this $effect)
    const tabChanged = currentTab?.id !== previousEditorTabId

    // CRITICAL: Clear isCreatingNewDocument when tab changes
    // This must happen BEFORE any other checks to prevent race conditions
    if (tabChanged && currentTab?.id) {
      isCreatingNewDocument = false
    }

    previousEditorTabId = currentTab?.id || null

    // Skip sync during document creation only (but not when tab changes!)
    if (isCreatingNewDocument && !tabChanged) {
      return
    }

    // Check if file was reloaded externally
    const wasReloaded = currentTab?.lastReloaded && currentTab.lastReloaded > lastReloadTime
    if (wasReloaded) {
      lastReloadTime = currentTab.lastReloaded
    }

    // Don't sync while user is actively typing, UNLESS tab changed or file was reloaded
    // FIXED: Improved isUserTyping check to include TipTap editor (ProseMirror)
    const isUserTyping = document.activeElement?.tagName === 'TEXTAREA' ||
                         document.activeElement?.classList?.contains('ProseMirror') ||
                         tipTapEditor?.isFocused
    const isTitleFocused = document.activeElement?.classList?.contains('title-input')

    if (currentTab) {
      // Always update if tab changed, file was reloaded, or if title field is focused (not content)
      if (tabChanged || wasReloaded || isTitleFocused || (currentTab.content !== editorText && !isUserTyping)) {
        // CRITICAL: Set flag to prevent textStore subscription from writing back to tab
        isSyncingTabContent = true
        // CRITICAL: Also clear isCreatingNewDocument when syncing tab content
        // This handles the case where user creates a new doc and immediately switches
        if (tabChanged) {
          isCreatingNewDocument = false
        }

        // Update both editorText and grammar store
        editorText = currentTab.content || ''
        grammarActions.setText(currentTab.content || '')

        // Force update TextManager if it exists
        if (textManager) {
          textManager.setText(currentTab.content || '')
        }

        // TipTap content is updated automatically via the content prop binding

        // Clear flag after animation frame to ensure TipTap has updated
        // Using requestAnimationFrame instead of queueMicrotask to give TipTap time to process
        requestAnimationFrame(() => {
          // Add one more frame for safety with {#key} recreation
          requestAnimationFrame(() => {
            isSyncingTabContent = false
          })
        })
      }
    } else {
      // No active tab - clear editor
      if (tabChanged || (editorText !== '' && !isUserTyping)) {
        isSyncingTabContent = true

        editorText = ''
        grammarActions.setText('')
        if (textManager) {
          textManager.setText('')
        }
        // TipTap content is updated automatically via the content prop binding

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isSyncingTabContent = false
          })
        })
      }
    }
  })
  
  // Update tab content when text changes
  let isUpdatingTab = false
  let isCreatingNewDocument = false  // Flag to prevent updates during document creation
  let previousTextUpdateTabId: string | null = null
  let lastHistoryUpdate = 0
  const historyDebounceTime = 1000 // 1 second debounce for history
  let isSyncingTabContent = false  // Flag to prevent circular updates during tab sync

  $effect(() => {
    const unsubscribe = textStore.subscribe(state => {
      // CRITICAL: Only block during tab content sync
      // Do NOT block based on isCreatingNewDocument if we're NOT on an Untitled tab
      // This prevents the flag from blocking updates when switching away from new docs
      const isOnNewUntitledTab = currentTab?.id?.startsWith('Untitled') && currentTab?.content === ''
      const shouldBlockForCreation = isCreatingNewDocument && isOnNewUntitledTab

      if (shouldBlockForCreation || isSyncingTabContent) {
        return
      }

      // Skip updates if we just switched tabs (prevents overwriting with empty content)
      // CRITICAL: Also skip if textStore content doesn't match current tab yet
      // This prevents previous tab's content from overwriting the newly opened tab
      if (currentTab && currentTab.id !== previousTextUpdateTabId) {
        previousTextUpdateTabId = currentTab.id
        return
      }

      // Only update if content actually changed and it's not empty when tab has content
      if (currentTab && state.current !== currentTab.content && !isUpdatingTab) {
        // Save cursor position before updating - only if user is typing
        let shouldRestoreCursor = false
        let savedCursorPosition = { start: 0, end: 0 }

        if (textareaElement && viewMode === 'source' && document.activeElement === textareaElement) {
          savedCursorPosition = {
            start: textareaElement.selectionStart,
            end: textareaElement.selectionEnd
          }
          shouldRestoreCursor = true
        }

        // Don't overwrite tab content with empty string if tab has content
        if (state.current === '' && currentTab.content && currentTab.content.length > 0) {
          return
        }

        // Don't overwrite newly created empty tab with previous tab's content
        // This prevents the bug where Cmd+N shows first tab's content
        if (state.current && state.current.length > 0 && currentTab.content === '') {
          // Check if this tab was just created (has no lastSaved time or very recent)
          const isNewTab = !currentTab.lastSaved || (Date.now() - currentTab.lastSaved.getTime() < 1000)
          if (isNewTab) {
            return
          }
        }

        isUpdatingTab = true
        fileStore.updateTabContent(currentTab.id, state.current)

        // Add to history with debouncing
        const now = Date.now()
        if (now - lastHistoryUpdate > historyDebounceTime) {
          fileStore.addToHistory(currentTab.id, state.current)
          lastHistoryUpdate = now
        }

        // Restore cursor position if needed
        if (shouldRestoreCursor) {
          requestAnimationFrame(() => {
            if (textareaElement && document.activeElement === textareaElement) {
              textareaElement.setSelectionRange(savedCursorPosition.start, savedCursorPosition.end)
            }
          })
        }

        // Reset flag after a short delay to prevent race conditions
        setTimeout(() => {
          isUpdatingTab = false
        }, 10)
      }
    })

    return unsubscribe
  })
  
  // Debouncing for duplicate check
  let duplicateCheckTimer: NodeJS.Timeout | null = null
  
  // Check for duplicate title
  async function checkTitleDuplicate(newTitle: string, clearIfNoDuplicate: boolean = true) {
    if (!newTitle || !currentTab) {
      if (clearIfNoDuplicate) {
        titleDuplicateWarning = ''
        hasDuplicateTitle = false
      }
      return false
    }
    
    const trimmedTitle = newTitle.trim()
    if (!trimmedTitle) {
      if (clearIfNoDuplicate) {
        titleDuplicateWarning = ''
        hasDuplicateTitle = false
      }
      return false
    }
    
    // Get the current saved name (from the file path)
    const currentPath = currentTab.id
    const lastSlash = currentPath.lastIndexOf('/')
    const currentSavedName = lastSlash === -1 ? currentPath : currentPath.substring(lastSlash + 1)
    
    // If typing back to the original saved name, clear warning
    if (currentSavedName === trimmedTitle) {
      titleDuplicateWarning = ''
      hasDuplicateTitle = false
      return false
    }
    
    // Get the directory path of current file
    const directory = lastSlash === -1 ? '' : currentPath.substring(0, lastSlash)
    
    // Check all open tabs first
    const state = get(fileStore)
    
    for (const tab of state.tabs.values()) {
      // Skip current tab
      if (tab.id === currentTab.id) continue
      
      // Get directory of this tab
      const tabLastSlash = tab.id.lastIndexOf('/')
      const tabDirectory = tabLastSlash === -1 ? '' : tab.id.substring(0, tabLastSlash)
      
      // Check if same directory and same title
      if (tabDirectory === directory && tab.title.toLowerCase() === trimmedTitle.toLowerCase()) {
        titleDuplicateWarning = `"${trimmedTitle}"은(는) 이미 이 위치에 존재합니다.`
        hasDuplicateTitle = true
        // Cancel auto-save when duplicate is detected
        fileStore.cancelAutoSave(currentTab.id)
        return true
      }
    }
    
    // Try to list files in the current directory using vaultStore
    try {
      const filesInDir = await vaultStore.listFiles(directory, false) // non-recursive, just this directory
      
      if (filesInDir && filesInDir.length > 0) {
        for (const file of filesInDir) {
          // Only check files, not directories
          if (file.type !== 'file') continue
          
          // Remove .md extension if present
          const fileName = file.name.endsWith('.md') ? file.name.slice(0, -3) : file.name
          
          if (fileName.toLowerCase() === trimmedTitle.toLowerCase()) {
            titleDuplicateWarning = `"${trimmedTitle}"은(는) 이미 이 위치에 존재합니다.`
            hasDuplicateTitle = true
            // Cancel auto-save when duplicate is detected
            fileStore.cancelAutoSave(currentTab.id)
            return true
          }
        }
      }
    } catch (err) {
      console.error('[checkTitleDuplicate] Error listing files:', err)
      // Continue with just the tab check as fallback
    }
    
    // Only clear warning if explicitly requested (not during typing)
    if (clearIfNoDuplicate) {
      titleDuplicateWarning = ''
      hasDuplicateTitle = false
    }
    return false
  }
  
  // Debounced version for oninput
  function checkTitleDuplicateDebounced(newTitle: string) {
    if (duplicateCheckTimer) {
      clearTimeout(duplicateCheckTimer)
    }
    
    // Handle empty/whitespace immediately
    if (!newTitle || !newTitle.trim()) {
      titleDuplicateWarning = ''
      hasDuplicateTitle = false
      return
    }
    
    // Do an immediate check for better responsiveness
    // This is a quick check just for open tabs
    const trimmedTitle = newTitle.trim()
    if (currentTab) {
      const currentPath = currentTab.id
      const lastSlash = currentPath.lastIndexOf('/')
      const currentSavedName = lastSlash === -1 ? currentPath : currentPath.substring(lastSlash + 1)
      const directory = lastSlash === -1 ? '' : currentPath.substring(0, lastSlash)
      
      // Quick check in open tabs (synchronous)
      const state = get(fileStore)
      let quickDuplicateFound = false
      
      for (const tab of state.tabs.values()) {
        if (tab.id === currentTab.id) continue
        const tabLastSlash = tab.id.lastIndexOf('/')
        const tabDirectory = tabLastSlash === -1 ? '' : tab.id.substring(0, tabLastSlash)
        
        if (tabDirectory === directory && tab.title.toLowerCase() === trimmedTitle.toLowerCase()) {
          titleDuplicateWarning = `"${trimmedTitle}"은(는) 이미 이 위치에 존재합니다.`
          hasDuplicateTitle = true
          fileStore.cancelAutoSave(currentTab.id)
          quickDuplicateFound = true
          break
        }
      }
      
      // If no quick duplicate found, we can clear the warning
      // (the full check will re-set it if needed)
      if (!quickDuplicateFound) {
        if (currentSavedName === trimmedTitle) {
          // Back to original name, clear warning
          titleDuplicateWarning = ''
          hasDuplicateTitle = false
        } else {
          // Temporarily clear, will be re-checked with file system
          titleDuplicateWarning = ''
          hasDuplicateTitle = false
        }
      }
    }
    
    // Then do the full check with file system (debounced)
    duplicateCheckTimer = setTimeout(async () => {
      await checkTitleDuplicate(newTitle, true)
    }, 200) // Reduced to 200ms for faster response
  }
  
  // File operations
  async function saveCurrentTab() {
    if (!currentTab) return

    // If title field is focused, update title first
    if (isTitleFocused && titleTextareaElement) {
      const newTitle = titleTextareaElement.value.trim()

      // Check for duplicate before saving
      if (newTitle && newTitle !== currentTab.title) {
        const hasDuplicate = await checkTitleDuplicate(newTitle)
        if (hasDuplicate) {
          console.warn('Cannot save: Duplicate file name exists')
          saveStatus = 'error'
          setTimeout(() => {
            saveStatus = 'idle'
          }, 2000)
          return
        }

        // Update title in store
        fileStore.updateTabTitle(currentTab.id, newTitle)
      }
    }

    // Don't save if there's a duplicate title warning
    if (hasDuplicateTitle) {
      console.warn('Cannot save: Duplicate file name exists')
      saveStatus = 'error'
      setTimeout(() => {
        saveStatus = 'idle'
      }, 2000)
      return
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('flush-editor-content'))
    }

    saveStatus = 'saving'
    try {
      // First check if we have permission
      const permission = await vaultStore.checkPermission()

      // If no permission, request it (this needs user gesture)
      if (permission !== 'granted') {
        const granted = await vaultStore.requestPermission()
        if (!granted) {
          errorService.showWarning('파일 저장 권한이 거부되었습니다. Vault 폴더를 다시 선택해주세요.')
          saveStatus = 'error'
          return
        }
      }

      await fileStore.saveTab(currentTab.id)
      saveStatus = 'saved'
      lastSaved = new Date()

      setTimeout(() => {
        saveStatus = 'idle'
      }, 2000)
    } catch (error) {
      // console.error('Failed to save:', error)
      saveStatus = 'error'
      errorService.showWarning('파일 저장에 실패했습니다. ' + (error as Error).message)
    }
  }
  
  async function createNewDocument() {
    // Set flag to prevent textStore updates during document creation
    isCreatingNewDocument = true

    try {
      // Clear editor text immediately before creating new document
      // This prevents content duplication in source mode
      editorText = ''
      if (textManager) {
        textManager.clearText()
      }
      grammarActions.reset()

      // Let fileStore handle the unique title generation
      // Don't pre-generate the title here as it causes duplication
      const docId = await fileStore.createNewDocument()
      await fileStore.openFile(docId)

      // Ensure textStore is completely reset after tab switch
      // This prevents any lingering content from previous tab
      grammarActions.setText('')
      editorText = ''

      // Focus the editor after creating new document
      await tick()

      if (viewMode === 'wysiwyg' && tipTapEditor) {
        // Focus TipTap editor (content is already cleared via editorText = '')
        tipTapEditor.focus()
      } else if (textareaElement) {
        // Focus textarea if in source mode
        textareaElement.focus()
        // Set cursor at the beginning
        textareaElement.setSelectionRange(0, 0)
      }

      // 새 문서를 열면 자동으로 포스팅 프롬프트 열기
      postingPromptStore.loadForTab(docId)
      unifiedPromptStore.show('posting')
    } finally {
      // Delay resetting the flag to ensure all updates are complete
      // This gives time for tab switching and all reactive updates to finish
      setTimeout(() => {
        isCreatingNewDocument = false
      }, 100)
    }
    
    // 상태 메시지 표시
    window.dispatchEvent(new CustomEvent('status-message', { 
      detail: { 
        message: '새 문서가 생성되었습니다. 포스팅 프롬프트를 설정해주세요.', 
        type: 'info' 
      } 
    }))
  }
  
  function toggleFileExplorer() {
    showFileExplorer = !showFileExplorer
    updateExplorerSettings({ isOpen: showFileExplorer })
  }
</script>

<!-- Controllers (Hidden components that manage business logic) -->
<AIFeatureController
  bind:this={aiController}
  bind:editorText
  {errorState}
  bind:hasChecked
  {correctionStates}
  {lastHistoryText}
  {textManager}
  {showSplitEditor}
  {viewMode}
  {tipTapEditor}
  {textareaElement}
  {inlineViewController}
  onStatusMessage={showStatus}
  onSplitViewEnter={handleSplitViewEnter}
  onSplitViewExit={handleSplitViewExit}
  onTextUpdate={handleTextUpdate}
  onTextReplace={handleTextReplace}
/>

<ClipboardController
  bind:this={clipboardController}
  bind:editorText
  {hasChecked}
  {textManager}
  {textareaElement}
  {tipTapEditor}
  {correctionStates}
  {lastHistoryText}
  {showSplitEditor}
  onStatusMessage={showStatus}
  onTextUpdate={handleTextUpdate}
  onClearErrors={handleClearErrors}
/>

<CorrectionController
  bind:this={correctionController}
  bind:editorText
  {errorState}
  bind:hasChecked
  bind:correctionStates
  {lastHistoryText}
  onStatusMessage={showStatus}
  onTextUpdate={handleTextUpdate}
  onUpdateLastHistoryText={(text) => {
    lastHistoryText = text
  }}
  onAllCorrectionsApplied={(count) => {
    allCorrectionsApplied = true
    processedErrorCount = count
    // Restore original view mode when all corrections are applied
    restoreOriginalViewMode()
  }}
/>

<GrammarCheckController
  bind:this={grammarCheckController}
  bind:editorText
  {errorState}
  {subscription}
  bind:hasChecked
  {correctionStates}
  {lastHistoryText}
  onStatusMessage={showStatus}
  onBatchProgress={(progress) => batchProgress = progress}
  onCheckingStateChange={(state) => {
    isGrammarChecking = state
  }}
  onCheckComplete={() => {
    // Store initial error count when check completes
    initialErrorCount = errorState.errors.length
    // Don't reset if corrections were just applied
    if (!allCorrectionsApplied) {
      allCorrectionsApplied = false
      processedErrorCount = 0
    }
    // Open sidebar to show grammar check results if there are errors
    if (errorState.errors.length > 0) {
      sidebarStore.open()
    }

    // Check if AI analysis is pending after grammar check
    if (pendingAIAnalysis) {
      pendingAIAnalysis = false
      // Wait a bit to ensure errors are loaded and UI is updated
      setTimeout(() => {
        handleAIAnalysis()
      }, 500)
    }
  }}
/>

<TextEditingController
  bind:this={textEditingController}
  bind:editorText
  {errorState}
  bind:hasChecked
  bind:correctionStates
  {textManager}
  bind:showSplitEditor
  onTextChange={(text, isCorrection) => {
    // Only reset all-applied state when text is empty or user manually edits (not corrections)
    if (!text || text.trim() === '') {
      allCorrectionsApplied = false
      processedErrorCount = 0
    } else if (!isCorrection && allCorrectionsApplied) {
      // User manually changed text after corrections were applied - reset state
      allCorrectionsApplied = false
      processedErrorCount = 0
    }
  }}
  onUndoRedo={(entry) => {}}
  onAISegmentsUpdate={(segments) => aiSegments = segments}
/>

<!-- Status Message Overlay with Batch Progress -->
<StatusMessage 
  message={statusMessage}
  type={batchProgress && statusMessage?.includes('배치') ? 'batch' :
        statusMessage?.includes('실패') || statusMessage?.includes('초과') ? 'error' : 
        statusMessage?.includes('복사') || statusMessage?.includes('문제가 없') || statusMessage?.includes('완료') ? 'success' : 
        statusMessage?.includes('발견') ? 'warning' : 'info'}
  progress={batchProgress}
  onClose={() => {
    statusMessage = null
    batchProgress = null
  }}
/>

<div class="main-editor-layout" class:demo-mode={isDemoMode}>
  <!-- Global Header (spans full width) -->
  <div
    class="global-header"
    class:demo-mode={isDemoMode}
    role="banner"
    aria-label="문서 작업 헤더"
  >
    <!-- Explorer Controls in Header (leftmost) -->
    <div class="header-explorer-controls" class:explorer-open={showFileExplorer} style="{showFileExplorer ? `width: ${explorerWidth + 40}px` : ''}">
      {#if !showFileExplorer}
        <!-- Collapsed: only toggle button -->
        <button
          class="explorer-toggle"
          onclick={toggleFileExplorer}
          title="탐색기 열기"
          aria-label="탐색기 열기"
        >
          <PanelLeft size={16} />
        </button>
      {:else}
        <!-- Expanded: tabs on left, toggle on right -->
        <div class="explorer-tabs-group">
          <button
            class="explorer-tab"
            class:active={explorerMode === 'explorer'}
            onclick={() => explorerMode = 'explorer'}
            title="파일 탐색기"
            aria-label="파일 탐색기"
            aria-pressed={explorerMode === 'explorer'}
          >
            <Folder size={16} />
          </button>
          <button
            class="explorer-tab"
            class:active={explorerMode === 'search'}
            onclick={() => explorerMode = 'search'}
            title="검색"
            aria-label="파일 검색"
            aria-pressed={explorerMode === 'search'}
          >
            <Search size={16} />
          </button>
        </div>
        <button
          class="explorer-toggle opened"
          onclick={toggleFileExplorer}
          title="탐색기 닫기"
          aria-label="탐색기 닫기"
        >
          <PanelLeft size={16} />
        </button>
      {/if}
    </div>
    
    <div class="tab-bar-wrapper">
      <TabBar onNewDocument={createNewDocument} />
    </div>
    <div class="header-right">
      <EditorHeader 
        bind:this={editorHeader}
        {stats} 
        {subscription} 
        isChecking={isGrammarChecking}
        onStatusMessage={(message) => {
          if (message) {
            showStatus(message)
          } else {
            statusMessage = null
          }
        }}
      />
    </div>
  </div>
  
  <!-- Main Content Below Header -->
  <div class="content-below-header">
    <!-- Toolbar (Always leftmost, below header) -->
    <Toolbar
      {editorText}
      onClear={handleClearText}
      onPaste={handlePasteText}
      onCopy={handleCopyText}
      onCut={handleCutText}
      onExportPDF={handleExportPDF}
      onOpenSettings={handleOpenSettings}
      onParaphrase={handleParaphrase}
      onContinue={handleContinue}
      onCritic={handleCritic}
      onExpand={handleExpand}
      onShrink={handleShrink}
      onSummarize={handleSummarize}
      onUnifiedPrompt={() => {
        unifiedPromptStore.toggle()
      }}
      isFileOpen={!!currentTab}
      onPrompt={() => {
        handleShowGenerationPrompt()
      }}
      hasSelection={hasTextSelection}
      {isIndividualSegmentUpdate}
      onToggleFullscreen={toggleFullscreen}
      {isFullscreen}
      hasGrammarCheckErrors={(() => {
        // Count only active errors (not ignored)
        const activeErrors = errorState.errors.filter(e => 
          !errorState.ignoredErrors.has(e.id)
        )
        return activeErrors.length > 0 && !allCorrectionsApplied
      })()}
    />
    
    <!-- File Explorer Panel -->
    {#if showFileExplorer}
      <aside
        class="explorer-panel"
        style="width: {explorerWidth}px"
        aria-label="파일 탐색 및 검색"
      >
        <!-- Always mount both components, control visibility with CSS -->
        <div
          class="explorer-content"
          class:hidden={explorerMode !== 'explorer'}
          aria-hidden={explorerMode !== 'explorer'}
        >
          <FileExplorer />
        </div>
        <div
          class="explorer-content"
          class:hidden={explorerMode !== 'search'}
          aria-hidden={explorerMode !== 'search'}
        >
          <FileSearchView />
        </div>

        <!-- Explorer Resize Handle -->
        <div
          bind:this={explorerResizeRef}
          class="explorer-resize-handle"
          class:resizing={isResizingExplorer}
          role="separator"
          aria-orientation="vertical"
          aria-label="파일 탐색기 너비 조절"
          aria-valuenow={explorerWidth}
          aria-valuemin="180"
          aria-valuemax="480"
        >
          <div class="resize-handle-line"></div>
        </div>
      </aside>
    {/if}
    
    <!-- Main Content Area -->
    <div
      class="main-content"
      style="margin-left: {showFileExplorer ? explorerWidth + 40 : 40}px"
      role="region"
      aria-label="문서 편집기"
    >
      <EditorContainer>
    {#snippet header()}
      <!-- Header is now at global level -->
    {/snippet}
  
  {#snippet toolbar()}
    <!-- Toolbar is now rendered outside EditorContainer -->
  {/snippet}
  
  {#snippet leftContent()}
    {#if showSplitEditor}
      <!-- Split Editor for AI features -->
      {@const yamlMatch = editorText.match(/^---[\r\n]+[\s\S]*?[\r\n]+---[\r\n]+/)}
      <SplitEditor
        originalText={get(aiFeaturesStore).selection?.text || editorText}
        yamlContent={yamlMatch ? yamlMatch[0] : undefined}
        mode={splitEditorMode || 'paraphrase'}
        segments={aiSegments}
        isProcessing={get(aiFeaturesStore).isProcessing}
        bind:isIndividualUpdate={isIndividualSegmentUpdate}
        isBulkProcessing={isBulkProcessing}
        onSegmentHover={handleSegmentHover}
        onPrompt={(segmentId) => {
          // Show generation prompt for split view segments
          showGenerationPrompt = true
          if (segmentId) {
            // If a specific segment ID is provided, use that segment's text
            const segment = aiSegments.find(s => s.id === segmentId)
            if (segment) {
              selectedTextForGeneration = segment.suggested || segment.original
            }
          } else {
            // Use all segments or selected text
            selectedTextForGeneration = get(aiFeaturesStore).selection?.text || editorText
          }
        }}
        onSegmentsChange={(segments) => {
          // Check if this is the first time segments are processed
          const hasProcessedSegments = segments.some(s => s.suggested && s.suggested !== '')
          const isStillProcessing = segments.some(s => s.isProcessing)
          
          // Initialize history when AI processing completes
          if (hasProcessedSegments && !isStillProcessing) {
            // Process each segment to ensure history is properly set
            const segmentsWithHistory = segments.map(seg => {
              // Only add to history if this is a new suggestion
              if (seg.suggested && (!seg.history || seg.history.length === 0)) {
                // Create initial history entry
                const initialHistory = [{
                  suggested: seg.suggested,
                  reason: seg.reason || '',
                  hasChanges: seg.hasChanges,
                  status: seg.status,
                  mode: seg.mode || splitEditorMode || 'paraphrase',
                  timestamp: Date.now(),
                  source: 'ai-action' as const
                }]
                return {
                  ...seg,
                  history: initialHistory,
                  historyIndex: 0
                }
              }
              // If history exists but current suggestion is not in history, add it
              else if (seg.suggested && seg.history && seg.history.length > 0) {
                const lastHistory = seg.history[seg.history.length - 1]
                // Check if current state is different from last history
                if (lastHistory.suggested !== seg.suggested) {
                  const newHistory = [...seg.history, {
                    suggested: seg.suggested,
                    reason: seg.reason || '',
                    hasChanges: seg.hasChanges,
                    status: seg.status,
                    mode: seg.mode || splitEditorMode || 'paraphrase',
                    timestamp: Date.now(),
                    source: 'ai-action' as const
                  }]
                  return {
                    ...seg,
                    history: newHistory,
                    historyIndex: newHistory.length - 1
                  }
                }
              }
              return seg
            })
            
            aiSegments = segmentsWithHistory
            aiFeaturesStore.setSegments(segmentsWithHistory)
            
            // Save state after initial processing
            if (!hasInitialAIState) {
              saveAISegmentsState('AI 분석 완료')
              hasInitialAIState = true
            }
          } else {
            // Just update segments without modifying history during processing
            aiSegments = segments
            aiFeaturesStore.setSegments(segments)
          }
        }}
        onSplitEditorApply={handleSplitEditorApply}
        onSplitEditorCancel={handleSplitEditorCancel}
        onBulkPrompt={handleSplitBulkPrompt}
      />
    {:else}
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
      <div 
        class="editor-wrapper" 
        role="application"
        aria-label="문서 본문 편집 영역"
        onclick={(e) => {
          // Close generation bar when clicking on editor area (but not on the generation bar itself)
          if (showGenerationPrompt && !e.target.closest('.generation-bar')) {
            showGenerationPrompt = false
            showSelectionOverlay = false
            selectedTextForGeneration = ''
            generationSelectionRange = null
            generationBarHeight = 0
            // Clear the selection highlight when closing the prompt
            if (viewMode === 'wysiwyg' && tipTapEditor) {
              tipTapEditor.clearSelectionHighlight()
            }
            const textarea = textareaElement
            if (textarea) {
              textarea.removeAttribute('data-has-selection')
              textarea.removeAttribute('data-selection-start')
              textarea.removeAttribute('data-selection-end')
            }
          }
        }}
        onkeydown={(e) => {
          // Handle Escape key to close generation bar
          if (e.key === 'Escape' && showGenerationPrompt) {
            showGenerationPrompt = false
            showSelectionOverlay = false
            selectedTextForGeneration = ''
            generationSelectionRange = null
            generationBarHeight = 0
            // Clear the selection highlight when closing the prompt
            if (viewMode === 'wysiwyg' && tipTapEditor) {
              tipTapEditor.clearSelectionHighlight()
            }
            const textarea = textareaElement
            if (textarea) {
              textarea.removeAttribute('data-has-selection')
              textarea.removeAttribute('data-selection-start')
              textarea.removeAttribute('data-selection-end')
            }
          }
        }}>
        {#if currentTab}
          <!-- Search Bar -->
          <SearchBar
            bind:value={searchTerm}
            currentMatch={searchState.currentMatchIndex + 1}
            totalMatches={searchState.matches.length}
            isVisible={searchState.isVisible}
            onSearch={handleSearch}
            onNext={handleSearchNext}
            onPrevious={handleSearchPrevious}
            onClose={handleSearchClose}
          />
          
          <!-- Title Input Field (hide for images) -->
          {#if !currentTab?.metadata?.isImage}
          <div class="title-editor" class:with-toolbar={viewMode === 'wysiwyg'}>
            <textarea
              bind:this={titleTextareaElement}
              class="title-input {titleDuplicateWarning ? 'has-warning' : ''}"
              value={currentTab.title || ''}
              aria-label="문서 제목"
              placeholder="제목을 입력하세요..."
              rows="1"
              onfocus={() => {
                isTitleFocused = true
                // Store original title when focusing
                originalTitle = currentTab?.title || ''
                // Clear any pending duplicate check
                if (duplicateCheckTimer) {
                  clearTimeout(duplicateCheckTimer)
                  duplicateCheckTimer = null
                }
              }}
              onblur={() => {
                isTitleFocused = false
                // Clear duplicate warning and flag when losing focus
                titleDuplicateWarning = ''
                hasDuplicateTitle = false
                // Cancel any pending duplicate check
                if (duplicateCheckTimer) {
                  clearTimeout(duplicateCheckTimer)
                  duplicateCheckTimer = null
                }
              }}
              oninput={(e) => {
                const textarea = e.currentTarget as HTMLTextAreaElement

                // Auto-resize height
                textarea.style.height = 'auto'
                textarea.style.height = textarea.scrollHeight + 'px'

                // Only check for duplicates, don't update store
                checkTitleDuplicateDebounced(textarea.value)
              }}
              onkeydown={async (e) => {
                // Stop propagation for undo/redo to prevent document handler
                if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey)) {
                  e.stopPropagation()
                  return
                }
                
                // Stop propagation for other shortcuts too
                if ((e.key === 'y' || e.key === 'Y') && (e.metaKey || e.ctrlKey)) {
                  e.stopPropagation()
                  return
                }
                
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()

                  const textarea = e.currentTarget as HTMLTextAreaElement
                  let finalTitle = textarea.value.trim()

                  if (!finalTitle && currentTab) {
                    finalTitle = await fileStore.generateUniqueTitle('untitled')
                    textarea.value = finalTitle
                  }

                  // Check for duplicate BEFORE updating the title
                  const hasDuplicate = await checkTitleDuplicate(finalTitle)

                  if (hasDuplicate) {
                    // If duplicate, revert to original title
                    if (originalTitle) {
                      textarea.value = originalTitle
                      // Clear warning after a brief display
                      setTimeout(() => {
                        titleDuplicateWarning = ''
                      }, 2000)
                    }
                    // Don't proceed with save or focus change
                    return
                  }

                  // Clear any warning if no duplicate
                  titleDuplicateWarning = ''

                  // Save the file (which will handle renaming if title changed)
                  if (currentTab && finalTitle) {
                    // Save with new title - this will update title and save in one operation
                    try {
                      await fileStore.saveTab(currentTab.id, finalTitle)
                    } catch (error) {
                      console.error('Failed to save on Enter:', error)
                      // Revert to original title if save failed
                      if (originalTitle) {
                        textarea.value = originalTitle
                        fileStore.updateTabTitle(currentTab.id, originalTitle)
                      }
                      return
                    }
                  }
                  
                  // Small delay to ensure IME composition is complete
                  setTimeout(() => {
                    // Focus the appropriate editor based on view mode
                    if (viewMode === 'wysiwyg' && tipTapEditor) {
                      tipTapEditor.focus('start')
                    } else if (textareaElement) {
                      textareaElement.focus()
                      // Move cursor to the beginning of the editor
                      textareaElement.setSelectionRange(0, 0)
                    }
                  }, 10)
                }
                
                // Handle Tab key to save and move to content
                if (e.key === 'Tab') {
                  e.preventDefault()

                  const textarea = e.currentTarget as HTMLTextAreaElement
                  let finalTitle = textarea.value.trim()

                  if (!finalTitle && currentTab) {
                    finalTitle = await fileStore.generateUniqueTitle('untitled')
                    textarea.value = finalTitle
                  }

                  // Check for duplicate BEFORE updating the title
                  const hasDuplicate = await checkTitleDuplicate(finalTitle)

                  if (hasDuplicate) {
                    // If duplicate, revert to original title
                    if (originalTitle) {
                      textarea.value = originalTitle
                      // Clear warning after a brief display
                      setTimeout(() => {
                        titleDuplicateWarning = ''
                      }, 2000)
                    }
                    // Don't proceed with save or focus change
                    return
                  }

                  // Clear any warning if no duplicate
                  titleDuplicateWarning = ''

                  // Only proceed if we have a valid title and current tab
                  if (currentTab && finalTitle) {
                    const titleChanged = currentTab.title !== finalTitle

                    // Save with new title if changed, or just save if dirty
                    if (titleChanged || currentTab.isDirty) {
                      try {
                        await fileStore.saveTab(currentTab.id, titleChanged ? finalTitle : undefined)
                        // After save completes, focus the editor
                        if (viewMode === 'wysiwyg' && tipTapEditor) {
                          tipTapEditor.focus('start')
                        } else if (textareaElement) {
                          textareaElement.focus()
                          textareaElement.setSelectionRange(0, 0)
                        }
                      } catch (error) {
                        console.error('Failed to save on Tab:', error)
                        // Revert to original title if save failed
                        if (originalTitle) {
                          textarea.value = originalTitle
                          fileStore.updateTabTitle(currentTab.id, originalTitle)
                        }
                        // Still move focus even if save failed
                        if (viewMode === 'wysiwyg' && tipTapEditor) {
                          tipTapEditor.focus('start')
                        } else if (textareaElement) {
                          textareaElement.focus()
                          textareaElement.setSelectionRange(0, 0)
                        }
                      }
                    } else {
                      // No changes to save, just move focus
                      if (viewMode === 'wysiwyg' && tipTapEditor) {
                        tipTapEditor.focus('start')
                      } else if (textareaElement) {
                        textareaElement.focus()
                        textareaElement.setSelectionRange(0, 0)
                      }
                    }
                  }
                }
              }}
            ></textarea>
            {#if titleDuplicateWarning}
              <div class="duplicate-warning">{titleDuplicateWarning}</div>
            {/if}
          </div>
          {/if}
          
          <!-- Text Manager handles state synchronization -->
          <TextManager bind:this={textManager} bind:value={editorText} />
          
          <!-- Reading Mode Toolbar (between title and content) -->
          {#if currentTab && viewMode === 'wysiwyg' && tipTapEditor && tipTapEditor.editor && !currentTab.metadata?.isImage}
            <ReadingModeToolbar editor={tipTapEditor.editor} />
          {/if}
          
          <!-- Editor container with relative positioning for overlay -->
          <div class="editor-content-container">
            <!-- Check if current tab is an image -->
            {#if currentTab?.metadata?.isImage}
              <!-- Render image viewer for image files -->
              <ImageViewer 
                imagePath={currentTab.metadata.imagePath || currentTab.id} 
                title={currentTab.title} 
              />
            {:else if viewMode === 'wysiwyg'}
              <!-- TipTap Markdown WYSIWYG Editor Mode -->
              {#key currentTab?.id}
                <TipTapMarkdownEditor
                  bind:this={tipTapEditor}
                  content={editorText}
                  onContentChange={(newContent) => {
                    editorText = newContent
                    handleTextUpdate(newContent)
                  }}
                  onSelectionChange={() => {
                    // Trigger selection check when selection changes in TipTap
                    if (window.checkSelectionForTipTap) {
                      window.checkSelectionForTipTap()
                    }
                  }}
                  onBackspaceAtStart={() => {
                    // Focus title when backspace at beginning of content
                    if (titleTextareaElement) {
                      titleTextareaElement.focus()
                      // Move cursor to end of title
                      titleTextareaElement.setSelectionRange(titleTextareaElement.value.length, titleTextareaElement.value.length)
                    }
                  }}
                  onParaphrase={handleParaphrase}
                  onExpand={handleExpand}
                  onShrink={handleShrink}
                  onFileDrop={async (fileId) => {
                    // Open the dropped file
                    await fileStore.openFile(fileId)
                  }}
                  placeholder="내용을 입력하세요..."
                  isPromptOpen={showGenerationPrompt || unifiedPromptHeight > 0}
                  promptHeight={generationBarHeight + unifiedPromptHeight}
                />
              {/key}
            {:else if errorState.errors.length === 0 && inlineViews.length === 0 && searchState.matches.length === 0}
              <!-- Use simple TextAreaEditor when there are no errors or inline views (Source Mode) -->
              <TextAreaEditor
                bind:value={editorText}
                bind:textareaElement
                onInput={() => {
                  // Handle text input - just update the text
                  handleTextUpdate(editorText)
                }}
                onDebouncedInput={() => {
                  // Debounced input - currently no auto-check
                  // Could add auto-check functionality here if needed
                }}
                onBackspaceAtStart={() => {
                  // Focus title when backspace at beginning of content
                  if (titleTextareaElement) {
                    titleTextareaElement.focus()
                    // Move cursor to end of title
                    titleTextareaElement.setSelectionRange(titleTextareaElement.value.length, titleTextareaElement.value.length)
                  }
                }}
                onClick={() => {
                  // Focus textarea when user clicks during search
                  if (searchState.isVisible && textareaElement) {
                    textareaElement.focus()
                  }
                }}
                placeholder="검사할 텍스트를 입력하세요..."
                class="input-editor"
                enableMarkdown={$settingsStore.editor.enableMarkdown}
              />
            {:else}
              <!-- Use TextDecorationEditor for accurate error positioning and hover effects -->
              <TextDecorationEditor
                bind:value={editorText}
                bind:textareaElement
                hoveredErrorId={hoveredErrorId}
                onBackspaceAtStart={() => {
                  // Focus title when backspace at beginning of content
                  if (titleTextareaElement) {
                    titleTextareaElement.focus()
                    // Move cursor to end of title
                    titleTextareaElement.setSelectionRange(titleTextareaElement.value.length, titleTextareaElement.value.length)
                  }
                }}
                errors={errorState.errors.filter(e => {
                  // Show error if:
                  // 1. Not ignored AND not kept-original (normal errors)
                  // 2. OR is kept-original (always show kept-original regardless of ignored state)
                  if (e.isOriginalKept || e.userAction === 'kept-original') {
                    return true  // Always show kept-original errors
                  }
                  return !errorState.ignoredErrors.has(e.id)  // Show non-ignored errors
                }).map(e => ({
                  ...e,
                  // Add start and end as direct properties
                  start: e.position.start,
                  end: e.position.end,
                  originalText: e.original || e.originalText  // Ensure originalText is available
                }))}
                placeholder="검사할 텍스트를 입력하세요..."
                enableMarkdown={$settingsStore.editor.enableMarkdown}
                onInput={() => {
                  // Handle text input
                  handleTextUpdate(editorText)
                }}
                onDebouncedInput={() => {
                  // Debounced input - currently no auto-check
                  // Could add auto-check functionality here if needed
                }}
                onErrorClick={(error) => {
                  // Apply first correction immediately when clicked
                  if (error.corrections && error.corrections.length > 0) {
                    handleApplyCorrection(error.id, error.corrections[0].id)
                  }
                }}
              />
            {/if}
            
            <!-- Selection Overlay for showing selection when prompt is open -->
            {#if viewMode === 'source' && textareaElement && showSelectionOverlay && generationSelectionRange}
              <SelectionOverlay
                {textareaElement}
                selectionStart={generationSelectionRange.start}
                selectionEnd={generationSelectionRange.end}
                isVisible={showSelectionOverlay}
              />
            {/if}
            
            <!-- Highlight Overlay is now handled within TextDecorationEditor -->
          </div>
        
        <!-- Inline View Controller - renders inline panels -->
        <InlineViewController
          bind:this={inlineViewController}
          {editorText}
          {tipTapEditor}
          {viewMode}
          onTextReplace={handleTextReplace}
          onStatusMessage={showStatus}
        />
        
        <!-- Unified Prompt Panel at bottom of editor -->
        <UnifiedPromptPanel 
          onHeightChange={(height) => {
            unifiedPromptHeight = height
          }}
        />
        
        <!-- Generation Bar at bottom of editor -->
        <GenerationBar
          isVisible={showGenerationPrompt}
          selectedText={selectedTextForGeneration}
          currentTabId={currentTab?.id}
          onGenerate={handleGenerate}
          onHeightChange={(height) => {
            generationBarHeight = height
          }}
          onCancel={() => {
            showGenerationPrompt = false
            showSelectionOverlay = false
            selectedTextForGeneration = ''
            generationSelectionRange = null
            
            // Focus back to editor
            setTimeout(() => {
              if (viewMode === 'wysiwyg' && tipTapEditor) {
                tipTapEditor.focus()
              } else if (textareaElement) {
                textareaElement.focus()
              }
            }, 100)
            generationBarHeight = 0
            // Clear the selection highlight when closing the prompt
            if (viewMode === 'wysiwyg' && tipTapEditor) {
              tipTapEditor.clearSelectionHighlight()
            }
            const textarea = textareaElement
            if (textarea) {
              textarea.removeAttribute('data-has-selection')
              textarea.removeAttribute('data-selection-start')
              textarea.removeAttribute('data-selection-end')
            }
          }}
        />
        {:else}
          <!-- Welcome Screen when no tabs are open -->
          <div class="welcome-screen">
            <div class="welcome-links">
              <button 
                class="welcome-link"
                onclick={async () => {
                  const id = await fileStore.createNewDocument()
                  await fileStore.openFile(id)
                  postingPromptStore.loadForTab(id)
                  unifiedPromptStore.show('posting')
                  
                  // 상태 메시지 표시
                  window.dispatchEvent(new CustomEvent('status-message', { 
                    detail: { 
                      message: '새 문서가 생성되었습니다. 포스팅 프롬프트를 설정해주세요.', 
                      type: 'info' 
                    } 
                  }))
                }}
              >
                새 문서 만들기 <span class="shortcut">(⌘N)</span>
              </button>
              <button
                class="welcome-link"
                onclick={() => {
                  // Check demo mode and iframe restrictions
                  const isDemo = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true'
                  const isInIframe = window.self !== window.top


                  if (isDemo) {
                    // Import toast here to show message
                    import('$lib/stores/toast').then(({ toast }) => {
                      toast.info('데모 모드에서는 로컬 파일을 열 수 없습니다. 좌측 파일 탐색기에서 샘플 문서를 열거나 새 문서를 만들 수 있습니다.', 5000)
                    })
                  }

                  showFileExplorer = true
                }}
              >
                파일 열기 <span class="shortcut">(⌘O)</span>
              </button>
            </div>
          </div>
        {/if}
      </div>
    {/if}
    
    <!-- View Mode Toggle Button (Hidden when SplitEditor is open) -->
    {#if !showSplitEditor}
      <ViewModeToggle 
        isPromptOpen={showGenerationPrompt || unifiedPromptHeight > 0} 
        promptHeight={generationBarHeight + unifiedPromptHeight} 
      />
    {/if}
  {/snippet}
  
  {#snippet rightContent()}
    <!-- Error Display handles all error rendering logic and AI mode -->
    <ErrorDisplay
      errors={errorState.errors.map(e => {
        return {
          ...e,
          // Add start and end as direct properties for consistency
          start: e.position.start,
          end: e.position.end,
          originalText: e.original || e.originalText
        }
      })}
      ignoredErrors={errorState.ignoredErrors}
      isChecking={isGrammarChecking}
      {hasChecked}
      hasText={!!editorText}
      allApplied={allCorrectionsApplied}
      processedCount={processedErrorCount}
      {initialErrorCount}
      onApplyCorrection={handleApplyCorrection}
      onApplyAlternative={handleApplyAlternative}
      onIgnoreError={handleIgnoreError}
      onKeepOriginal={handleKeepOriginal}
      onApplyAll={handleApplyAllCorrections}
      onRejectAll={handleRejectAllCorrections}
      onErrorHover={(errorId) => {
        hoveredErrorId = errorId
      }}
      onErrorUnhover={() => {
        hoveredErrorId = null
      }}
      onCancelCheck={async () => {
        // Cancel grammar check if it's running
        const { grammarCheckService } = await import('$lib/services/GrammarCheckServiceV3')
        grammarCheckService.cancel()
        
        // Cancel AI analysis if it's running
        const aiService = aiController?.getAIAnalysisService?.()
        if (aiService) {
          aiService.cancel()
        }
        
        // Also cancel batch AI analysis
        const { aiSuggestionsStore } = await import('$lib/stores/aiSuggestions')
        const aiAnalysisService = aiSuggestionsStore.getAIAnalysisService()
        if (aiAnalysisService) {
          aiAnalysisService.cancel()
        }
      }}
      isAIMode={showSplitEditor}
      aiSegments={aiSegments || []}
      aiMode={splitEditorMode || 'paraphrase'}
      isAIAnalyzing={$aiFeaturesStore.isProcessing}
      onAIApply={handleAIApply}
      onAIIgnore={handleAIIgnore}
      onAIContinue={handleAIContinue}
      onAIExpand={handleAIExpand}
      onAIShrink={handleAIShrink}
      onAIParaphrase={handleAIParaphrase}
      onAIPrompt={handleAIPrompt}
      onAISegmentPromptGenerate={handleAISegmentPromptGenerate}
      onAIApplyAll={handleAIApplyAll}
      onAIRejectAll={handleAIRejectAll}
      onAIApplyAndExit={handleAIApplyAndExit}
      onAIContinueEditing={handleAIContinueEditing}
      onAIContinueAll={handleAIContinueAll}
      onAIParaphraseAll={handleAIParaphraseAll}
      onAIExpandAll={handleAIExpandAll}
      onAIShrinkAll={handleAIShrinkAll}
      onAIPromptAll={handleAIPromptAll}
      onSegmentHover={handleSegmentHover}
      onSegmentUndo={handleSegmentUndo}
      onSegmentRedo={handleSegmentRedo}
      canSegmentUndo={canSegmentUndo}
      canSegmentRedo={canSegmentRedo}
      inlineViewCards={inlineViewCards}
      onInlineViewApply={handleInlineViewApply}
      onInlineViewIgnore={handleInlineViewIgnore}
      onInlineViewParaphrase={handleInlineViewParaphrase}
      onInlineViewExpand={handleInlineViewExpand}
      onInlineViewShrink={handleInlineViewShrink}
      onInlineViewContinue={handleInlineViewContinue}
      onInlineViewPrompt={handleInlineViewPrompt}
      onInlineViewGeneratePrompt={handleInlineViewGeneratePrompt}
      onInlineViewUndo={handleInlineViewUndo}
      onInlineViewRedo={handleInlineViewRedo}
      canInlineViewUndo={canInlineViewUndo}
      canInlineViewRedo={canInlineViewRedo}
    />
  {/snippet}
      {#snippet footer()}
        <EditorFooter
          {editorText}
          isChecking={isGrammarChecking}
          {hasChecked}
          hasErrors={stats.hasErrors}
          errorCount={stats.totalErrors}
          totalErrorsIncludingKept={errorState.errors.length}
          canCheck={true}
          errors={errorState.errors.map(e => ({
            ...e,
            // Add start and end as direct properties for consistency
            start: e.position.start,
            end: e.position.end,
            originalText: e.original || e.originalText
          }))}
          saveStatus={currentTab?.isDirty ? 'idle' : (currentTab?.showSaveStatus ? 'saved' : 'idle')}
          lastSaved={currentTab?.showSaveStatus ? currentTab?.lastSaved : null}
          onCheck={showSplitEditor ? () => {} : handleCheckGrammar}
          onAIAnalysis={handleAIAnalysis}
          onCancelCheck={async () => {
            // Cancel grammar check if it's running
            const { grammarCheckService } = await import('$lib/services/GrammarCheckServiceV3')
            grammarCheckService.cancel()
            
            // Cancel AI analysis if it's running
            const aiService = aiController?.getAIAnalysisService()
            if (aiService) {
              aiService.cancel()
            }
            
            // Also cancel batch AI analysis
            const { aiSuggestionsStore } = await import('$lib/stores/aiSuggestions')
            const aiAnalysisService = aiSuggestionsStore.getAIAnalysisService()
            if (aiAnalysisService) {
              aiAnalysisService.cancel()
            }
          }}
          isInSplitView={showSplitEditor}
          hasInlineViews={inlineViews.length > 0}
          {statusMessage}
        />
      {/snippet}
      </EditorContainer>
    </div>
  </div>
</div>

<!-- File Change Modal -->
<FileChangeModal 
  bind:isOpen={showFileChangeModal}
  changedFiles={fileChangeCount}
  {needsPermission}
  on:reload-files={handleReloadFiles}
  on:refresh-page={() => {
    showFileChangeModal = false
    // Save current state before refresh
    if (currentTab) {
      sessionStorage.setItem('pendingVaultRestore', JSON.stringify({
        isElectron: false,
        name: get(vaultStore).vaultName
      }))
    }
    window.location.reload()
  }}
  on:request-permission={handleRequestPermission}
/>

<!-- PDF Export Dialog -->
<PDFExportDialog 
  bind:isOpen={showPDFExport}
  content={editorText}
  fileName={currentTab?.title || currentTab?.name || ''}
/>

<!-- Empty Line Indicator -->

<style>
  /* Main Layout */
  .main-editor-layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    position: relative;
  }

  /* Demo mode - fill iframe height */
  .main-editor-layout.demo-mode {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* Global Header - spans full width */
  .global-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 36px;
    display: flex;
    align-items: center;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-primary);
    z-index: 2000;
    -webkit-app-region: drag;
    min-width: 0; /* Allow proper shrinking */
    overflow: hidden; /* Prevent content from overflowing */
  }

  /* Demo mode - keep standard header height */
  @media (max-width: 767px) {
    .global-header.demo-mode {
      height: 32px; /* Slightly reduce header height on mobile demo */
    }
  }

  .global-header :global(.tab-bar) {
    flex: 1;
  }
  
  /* Tab bar wrapper */
  .tab-bar-wrapper {
    flex: 1 1 auto;
    display: flex;
    min-width: 200px; /* Minimum width for tab bar */
    overflow: hidden;
  }
  
  /* Header Explorer Controls */
  .header-explorer-controls {
    display: flex;
    align-items: center;
    -webkit-app-region: no-drag;
    position: relative;
  }
  
  .header-explorer-controls:not(.explorer-open) {
    width: auto; /* Just fit the toggle button */
    justify-content: flex-start;
    padding: 0 8px;
    margin-left: 68px; /* Space for traffic lights on macOS */
    flex-shrink: 0; /* Don't shrink */
  }
  
  .header-explorer-controls.explorer-open {
    /* Width is set dynamically via inline style */
    padding-left: 76px; /* 68px (traffic lights) + 8px padding */
    padding-right: 8px;
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-primary);
    justify-content: space-between;
    flex-shrink: 0; /* Don't shrink */
  }
  
  .explorer-tabs-group {
    display: flex;
    gap: 4px;
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-right: 12px;
    -webkit-app-region: no-drag;
    flex-shrink: 0; /* Don't shrink when tabs are many */
  }
  
  /* Content Below Header */
  .content-below-header {
    flex: 1;
    display: flex;
    min-height: 0; /* Important for nested flexbox */
    min-width: 0; /* Prevent horizontal overflow */
    overflow: hidden;
    margin-top: 36px; /* Account for fixed header */
  }

  /* Demo mode - content area flex with no gap */
  .demo-mode .content-below-header {
    margin-top: 36px; /* Keep editor content below the fixed header */
    flex: 1;
    overflow: hidden;
    display: flex;
    min-height: 0;
  }

  /* Demo mode mobile - adjust for smaller header */
  @media (max-width: 767px) {
    .demo-mode .content-below-header {
      margin-top: 32px;
    }
  }

  /* Position the Toolbar as the leftmost element below header */
  .content-below-header > :global(.toolbar) {
    position: fixed;
    top: 36px; /* Below header */
    left: 0;
    z-index: 1000;
    height: calc(100vh - 36px);
  }
  
  
  
  /* Explorer toggle button in header */
  .header-explorer-controls .explorer-toggle {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    pointer-events: auto;
  }
  
  .header-explorer-controls .explorer-toggle:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--text-primary);
  }
  
  :global(.dark-theme) .header-explorer-controls .explorer-toggle:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  /* Rotate toggle icon when opened */
  .header-explorer-controls .explorer-toggle.opened :global(svg) {
    transform: rotate(180deg);
  }
  
  /* Explorer tab buttons in header */
  .header-explorer-controls .explorer-tab {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .header-explorer-controls .explorer-tab:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--text-primary);
  }
  
  :global(.dark-theme) .header-explorer-controls .explorer-tab:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .header-explorer-controls .explorer-tab.active {
    background: var(--bg-primary);
    color: var(--accent-primary);
  }
  
  /* Removed traffic-light-spacer as it's no longer needed */
  /* Removed unused .controls-spacer */
  
  /* Explorer Panel */
  .explorer-panel {
    position: fixed;
    top: 36px; /* Below global header only */
    left: 40px; /* After toolbar */
    height: calc(100vh - 36px);
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-primary);
    display: flex;
    flex-direction: column;
    z-index: 800;
  }

  .explorer-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .explorer-content.hidden {
    display: none;
  }
  
  /* Explorer Resize Handle */
  .explorer-resize-handle {
    position: absolute;
    right: -3px;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    z-index: 1000;
    background: transparent;
    transition: background 0.2s ease;
  }
  
  .explorer-resize-handle:hover {
    background: rgba(124, 58, 237, 0.1);
  }
  
  .explorer-resize-handle.resizing {
    background: rgba(124, 58, 237, 0.15);
  }
  
  .explorer-resize-handle .resize-handle-line {
    position: absolute;
    right: 2px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--border-primary);
    transition: all 0.2s ease;
  }
  
  .explorer-resize-handle:hover .resize-handle-line {
    background: var(--accent-primary);
    width: 2px;
    right: 2px;
  }
  
  .explorer-resize-handle.resizing .resize-handle-line {
    background: var(--accent-primary);
    width: 3px;
    right: 1px;
  }
  
  /* Search View - Now handled by FileSearchView component */
  
  /* Main Content */
  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-left: 40px; /* Offset by toolbar only */
    min-height: 0; /* Important for flexbox */
    overflow: hidden; /* Prevent overflow */
    min-width: 0; /* Prevent horizontal overflow */
  }

  /* Demo mode - main content flex */
  .demo-mode .main-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* No need for :has selector anymore - will use dynamic margin in template */
  
  /* Header Container - removed as header is now global */
  
  .editor-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    width: 100%;
    box-sizing: border-box;
  }
  
  /* Maintain text selection highlight when generation bar is open */
  :global(textarea[data-has-selection="true"]) {
    caret-color: transparent;
  }
  
  :global(textarea[data-has-selection="true"]::selection) {
    background: rgba(255, 235, 59, 0.4);
    color: inherit;
  }
  
  :global(textarea[data-has-selection="true"]:not(:focus)::selection) {
    background: rgba(255, 235, 59, 0.4);
    color: inherit;
  }
  
  /* Visual highlight for selected text */
  :global(textarea[data-has-selection="true"]:focus) {
    outline: none;
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    :global(textarea[data-has-selection="true"]::selection),
    :global(textarea[data-has-selection="true"]:not(:focus)::selection) {
      background: rgba(99, 102, 241, 0.4);
    }
  }
  
  /* Keep selection visible even when textarea loses focus */
  :global(textarea)::selection {
    background: rgba(99, 102, 241, 0.3);
  }
  
  :global(textarea)::-webkit-selection {
    background: rgba(99, 102, 241, 0.3);
  }
  
  /* When generation prompt is shown, keep selection visible */
  :global(textarea:not(:focus))::selection {
    background: rgba(99, 102, 241, 0.25);
  }
  
  :global(textarea:not(:focus))::-webkit-selection {
    background: rgba(99, 102, 241, 0.25);
  }
  
  :global(.dark) :global(textarea)::selection {
    background: rgba(139, 92, 246, 0.3);
  }
  
  :global(.dark) :global(textarea)::-webkit-selection {
    background: rgba(139, 92, 246, 0.3);
  }
  
  /* Title Editor Styles */
  .title-editor {
    position: relative;
    width: 100%;
    margin-bottom: 12px;
    padding: 8px max(32px, calc(50% - 500px));
    border-bottom: 1px solid var(--border-primary);
    box-sizing: border-box;
    overflow: visible; /* Allow warning to show outside */
    display: flex;
    align-items: center; /* Vertically center the title input */
    min-height: 60px; /* Ensure sufficient height */
  }

  /* Demo mode - standard layout */
  :global(.demo-mode) .title-editor {
    margin-bottom: 8px;
    padding: 8px max(24px, calc(50% - 500px));
    min-height: 48px;
  }

  /* Demo mode mobile - compact title area */
  @media (max-width: 767px) {
    :global(.demo-mode) .title-editor {
      padding: 8px 12px; /* Even more compact for iframe */
      min-height: 40px; /* Smaller height */
      margin-bottom: 4px;
    }

    :global(.demo-mode) .title-input {
      font-size: 1.05em; /* Smaller in demo */
      line-height: 1.6;
    }
  }
  
  .title-editor.with-toolbar {
    margin-bottom: 0;
  }
  
  /* Editor content container for proper overlay positioning */
  .editor-content-container {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden; /* Prevent overflow here - let child handle it */
  }
  
  /* TextAreaEditor and TextDecorationEditor handle their own scrolling */
  
  .title-input {
    width: 100%;
    max-width: 1000px;
    padding: 12px 0;
    font-size: 1.3em;
    font-weight: 600;
    line-height: 1.4;
    color: var(--text-primary);
    background: transparent;
    border: none;
    outline: none;
    font-family: inherit;
    resize: none;
    overflow: hidden;
    min-height: 1.4em;
    word-wrap: break-word;
    white-space: pre-wrap;
    vertical-align: middle; /* Prevent baseline alignment issues in Safari */
    -webkit-appearance: none; /* Remove Safari default styling */
  }
  
  .title-input::placeholder {
    color: var(--text-tertiary);
    opacity: 0.5;
  }
  
  .title-input:focus {
    outline: none;
  }
  
  /* Dark mode title input */
  :global(.dark-theme) .title-input {
    color: var(--text-primary);
  }
  
  @media (prefers-color-scheme: dark) {
    :global(.auto-theme) .title-input {
      color: var(--text-primary);
    }
  }
  
  /* Title input warning styles */
  
  .duplicate-warning {
    position: absolute;
    top: calc(100% + 4px); /* Position below the border */
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 12px;
    background: var(--error, #ef4444);
    color: white;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    z-index: 1000;
    white-space: nowrap;
    width: fit-content;
    max-width: 90%;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3),
                0 2px 4px rgba(0, 0, 0, 0.2);
    animation: warningFadeIn 0.2s ease-out;
    line-height: 1.4;
  }

  @keyframes warningFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -4px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  
  /* Welcome Screen Styles - Minimal */
  .welcome-screen {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: var(--bg-primary);
  }
  
  .welcome-links {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  
  .welcome-link {
    background: none;
    border: none;
    color: var(--accent-primary);
    cursor: pointer;
    padding: 0;
    font-size: 16px;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }
  
  .welcome-link:hover {
    color: var(--accent-primary-hover);
    text-decoration-thickness: 2px;
  }
  
  .shortcut {
    color: var(--accent-primary);
    font-size: 16px;
    font-weight: normal;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    opacity: 0.7;
  }
  
  .welcome-link:hover .shortcut {
    color: var(--accent-primary-hover);
    text-decoration-thickness: 2px;
    opacity: 0.7;
  }
  /* Mobile responsive - optimize title editor for small screens */
  @media (max-width: 767px) {
    .title-editor {
      padding: 10px 16px; /* Reduced vertical padding */
      margin-bottom: 6px;
      min-height: 44px; /* Smaller height */
      align-items: center;
    }

    .title-input {
      padding: 0;
      font-size: 1.1em; /* Smaller font */
      line-height: 1.8; /* Reduced line-height but still creates space */
      min-height: auto;
    }
  }

  /* Tablet responsive */
  @media (min-width: 768px) and (max-width: 1279px) {
    .title-editor {
      padding: 14px 24px 12px 24px;
      min-height: 58px;
    }

    .title-input {
      font-size: 1.2em;
      line-height: 1.45;
      padding: 8px 0 6px 0;
    }
  }
</style>
